// src/hooks/useOnLongHover.ts
import { useCallback as useCallback2, useEffect as useEffect4, useState } from "react";

// src/hooks/useFreshCallback.ts
import { useCallback } from "react";

// src/hooks/useFreshRef.ts
import { useEffect as useEffect2, useRef } from "react";

// src/hooks/useIsomorphicEffect.ts
import { useEffect, useLayoutEffect } from "react";
var useIsomorphicEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// src/hooks/useFreshRef.ts
function useFreshRef(value, preferLayoutEffect = false) {
  const useEffectToUse = preferLayoutEffect ? useIsomorphicEffect : useEffect2;
  const ref = useRef(value);
  useEffectToUse(() => {
    ref.current = value;
  });
  return ref;
}

// src/hooks/useFreshCallback.ts
function useFreshCallback(callback) {
  const freshRef = useFreshRef(callback);
  const tick = useCallback(
    (...args) => {
      return freshRef.current(...args);
    },
    [freshRef]
  );
  return tick;
}

// src/hooks/useTimeoutWhen.ts
import { useEffect as useEffect3 } from "react";

// src/utils/noop.ts
var noop = () => {
};

// src/hooks/useTimeoutWhen.ts
function useTimeoutWhen(callback, timeoutDelayMs = 0, when = true, key = 0) {
  const freshCallback = useFreshCallback(callback);
  useEffect3(() => {
    if (when) {
      let internalCallback2 = function() {
        freshCallback();
      };
      var internalCallback = internalCallback2;
      if (typeof window !== "undefined") {
        const timeout = window.setTimeout(internalCallback2, timeoutDelayMs);
        return () => {
          window.clearTimeout(timeout);
        };
      } else {
        console.warn("useTimeoutWhen: window is undefined.");
      }
    }
    return noop;
  }, [timeoutDelayMs, when, key, freshCallback]);
}

// src/hooks/useOnLongHover.ts
var useOnLongHover = (callback, { duration = 300 } = {}) => {
  const [targetNode, setTargetNode] = useState(null);
  const ref = useCallback2((node) => {
    setTargetNode(node);
  }, []);
  const [isHovering, setIsHovering] = useState(false);
  const freshCallback = useFreshCallback(callback);
  useTimeoutWhen(
    () => {
      freshCallback();
    },
    duration,
    isHovering
  );
  const start = useCallback2(() => {
    setIsHovering(true);
  }, []);
  const handleOnMouseLeave = useCallback2((_) => {
    setIsHovering(false);
  }, []);
  useEffect4(() => {
    if (targetNode) {
      targetNode.addEventListener("mouseenter", start);
      targetNode.addEventListener("mouseleave", handleOnMouseLeave);
    }
    return () => {
      if (targetNode) {
        targetNode.removeEventListener("mouseenter", start);
        targetNode.removeEventListener("mouseleave", handleOnMouseLeave);
      }
    };
  }, [start, handleOnMouseLeave, targetNode]);
  return ref;
};

// src/hooks/useOnLongPress.ts
import { useCallback as useCallback3, useEffect as useEffect5, useState as useState2 } from "react";
var defaultOnClick = () => {
};
var useOnLongPress = (callback, { onClick, duration = 300 } = {}) => {
  const [targetNode, setTargetNode] = useState2(null);
  const ref = useCallback3((node) => {
    setTargetNode(node);
  }, []);
  const [isPressing, setIsPressing] = useState2(false);
  const freshCallback = useFreshCallback(callback);
  const freshClick = useFreshCallback(onClick ?? defaultOnClick);
  useTimeoutWhen(freshCallback, duration, isPressing);
  const start = useCallback3((_) => {
    setIsPressing(true);
  }, []);
  const handleOnClick = useCallback3(
    (event) => {
      setIsPressing(false);
      freshClick(event);
    },
    [freshClick]
  );
  useEffect5(() => {
    if (targetNode) {
      targetNode.addEventListener("mousedown", start);
      targetNode.addEventListener("mouseup", handleOnClick);
      targetNode.addEventListener("mouseleave", handleOnClick);
      targetNode.addEventListener("touchstart", start);
      targetNode.addEventListener("touchend", handleOnClick);
      targetNode.addEventListener("touchcancel", handleOnClick);
    }
    return () => {
      if (targetNode) {
        targetNode.removeEventListener("mousedown", start);
        targetNode.removeEventListener("mouseup", handleOnClick);
        targetNode.removeEventListener("mouseleave", handleOnClick);
        targetNode.removeEventListener("touchstart", start);
        targetNode.removeEventListener("touchend", handleOnClick);
        targetNode.removeEventListener("touchcancel", handleOnClick);
      }
    };
  }, [start, handleOnClick, targetNode]);
  return ref;
};

// src/hooks/useMapState.ts
import { useCallback as useCallback4, useState as useState3 } from "react";
function useMapState(initialValue) {
  const [map, setMap] = useState3(initialValue);
  const set = useCallback4((key, value) => {
    setMap((currentMap) => ({
      ...currentMap,
      [key]: value
    }));
  }, []);
  const has = useCallback4(
    (key) => {
      return typeof map[key] !== "undefined";
    },
    [map]
  );
  const setMultiple = useCallback4((nextMap) => {
    setMap((currentMap) => ({
      ...currentMap,
      ...nextMap
    }));
  }, []);
  const removeMultiple = useCallback4(
    (...keys) => {
      setMap((currentMap) => {
        const nextMap = { ...currentMap };
        for (const key of keys) {
          delete nextMap[key];
        }
        return nextMap;
      });
    },
    [setMap]
  );
  const remove = useCallback4(
    (key) => {
      setMap((currentMap) => {
        const nextMap = { ...currentMap };
        delete nextMap[key];
        return nextMap;
      });
    },
    [setMap]
  );
  const removeAll = useCallback4(() => {
    setMap((currentMap) => {
      const nextMap = { ...currentMap };
      for (const key in nextMap) {
        if (nextMap.hasOwnProperty(key)) {
          delete nextMap[key];
        }
      }
      return nextMap;
    });
  }, [setMap]);
  return [
    map,
    {
      has,
      remove,
      removeAll,
      removeMultiple,
      set,
      setMultiple
    }
  ];
}

// src/hooks/useArrayState.ts
import { useCallback as useCallback5, useMemo, useState as useState4 } from "react";
function useArrayState(initial) {
  const [array, setArray] = useState4(initial ?? []);
  const push = useCallback5(
    (value) => {
      setArray([...array, value]);
    },
    [array]
  );
  const pop = useCallback5(() => {
    setArray(array.slice(0, array.length - 1));
  }, [array]);
  const clear = useCallback5(() => {
    setArray([]);
  }, []);
  const unshift = useCallback5(
    (value) => {
      setArray([value, ...array]);
    },
    [array]
  );
  const shift = useCallback5(() => {
    setArray(array.slice(1));
  }, [array]);
  const reverse = useCallback5(() => {
    setArray([...array].reverse());
  }, [array]);
  const concat = useCallback5(
    (value) => {
      setArray([...array, ...value]);
    },
    [array]
  );
  const fill = useCallback5(
    (value, start, end) => {
      setArray([...array].fill(value, start, end));
    },
    [array]
  );
  const updateItemAtIndex = useCallback5(
    (index, value) => {
      setArray((prevArray) => {
        const newArray = [...prevArray];
        newArray[index] = value;
        return newArray;
      });
    },
    [setArray]
  );
  const splice = useCallback5(
    (...args) => {
      setArray((prevArray) => {
        const newArray = [...prevArray];
        newArray.splice(...args);
        return newArray;
      });
    },
    [setArray]
  );
  const removeItemAtIndex = useCallback5(
    (index) => {
      setArray((prevArray) => {
        const newArray = [...prevArray];
        newArray.splice(index, 1);
        return newArray;
      });
    },
    [setArray]
  );
  const replaceItemAtIndex = useCallback5(
    (index, value) => {
      setArray((prevArray) => {
        const newArray = [...prevArray];
        newArray.splice(index, 1, value);
        return newArray;
      });
    },
    [setArray]
  );
  const insertItemAtIndex = useCallback5(
    (index, value) => {
      setArray((prevArray) => {
        const newArray = [...prevArray];
        newArray.splice(index, 0, value);
        return newArray;
      });
    },
    [setArray]
  );
  const sort = useCallback5(
    (compareFn) => {
      setArray([...array].sort(compareFn));
    },
    [array]
  );
  const controls = useMemo(() => {
    return {
      push,
      pop,
      clear,
      unshift,
      shift,
      reverse,
      concat,
      fill,
      updateItemAtIndex,
      setArray,
      splice,
      removeItemAtIndex,
      replaceItemAtIndex,
      insertItemAtIndex,
      sort
    };
  }, [
    push,
    pop,
    clear,
    unshift,
    shift,
    reverse,
    concat,
    fill,
    updateItemAtIndex,
    setArray,
    splice,
    removeItemAtIndex,
    replaceItemAtIndex,
    insertItemAtIndex,
    sort
  ]);
  const returnValue = useMemo(() => {
    return [array, controls];
  }, [array, controls]);
  return returnValue;
}

// src/hooks/useAsyncEffect.ts
import { useEffect as useEffect7, useRef as useRef3, useCallback as useCallback7 } from "react";

// src/hooks/useGetIsMounted.ts
import { useCallback as useCallback6, useEffect as useEffect6, useRef as useRef2 } from "react";
var useGetIsMounted = () => {
  const isMountedRef = useRef2(false);
  const get = useCallback6(() => isMountedRef.current, []);
  useEffect6(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return get;
};

// src/hooks/useAsyncEffect.ts
function useAsyncEffect(effect, deps, cleanup) {
  const lastCallId = useRef3(0);
  const getIsMounted = useGetIsMounted();
  const effectRef = useFreshRef(effect);
  const callback = useCallback7(async () => {
    const callId = ++lastCallId.current;
    const shouldContinueEffect = () => {
      return getIsMounted() && callId === lastCallId.current;
    };
    try {
      return await effectRef.current(shouldContinueEffect);
    } catch (error) {
      throw error;
    }
  }, [getIsMounted, ...deps]);
  useEffect7(() => {
    let result;
    callback().then((value) => {
      result = value;
    });
    return () => {
      cleanup?.(result);
    };
  }, [callback, cleanup]);
}

// src/hooks/useAudio.ts
import { useState as useState5, useEffect as useEffect8 } from "react";
var noop2 = () => {
};
function useAudio(options = {}, callbacks = {}) {
  const { autoPlay = false, isMuted: initialIsMuted = false } = options;
  const [isPlaying, setIsPlaying] = useState5(autoPlay);
  const [isMuted, setIsMuted] = useState5(initialIsMuted);
  const [audioNode, setAudioNode] = useState5(null);
  const onPlay = useFreshCallback(callbacks.onPlay ?? noop2);
  const onPause = useFreshCallback(callbacks.onPause ?? noop2);
  const onMute = useFreshCallback(callbacks.onMute ?? noop2);
  const onUnmute = useFreshCallback(callbacks.onUnmute ?? noop2);
  const onLoadedMetadata = useFreshCallback(callbacks.onLoadedMetadata ?? noop2);
  const audioCallbackRef = (node) => {
    if (node !== null) {
      setAudioNode(node);
    }
  };
  useEffect8(() => {
    if (!audioNode) return;
    if (isPlaying) {
      audioNode.play();
    } else {
      audioNode.pause();
    }
  }, [audioNode, isPlaying]);
  useEffect8(() => {
    if (!audioNode) return;
    audioNode.muted = isMuted;
  }, [audioNode, isMuted]);
  useEffect8(() => {
    if (!audioNode) return;
    const handleLoadedMetadata = () => {
      if (autoPlay) {
        audioNode?.play();
      }
      onLoadedMetadata();
    };
    const handlePlay = () => {
      onPlay();
    };
    const handlePause = () => {
      onPause();
    };
    const handleEnded = () => {
      setIsPlaying(false);
    };
    audioNode?.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioNode?.addEventListener("play", handlePlay);
    audioNode?.addEventListener("pause", handlePause);
    audioNode?.addEventListener("ended", handleEnded);
    return () => {
      audioNode?.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audioNode?.removeEventListener("play", handlePlay);
      audioNode?.removeEventListener("pause", handlePause);
      audioNode?.removeEventListener("ended", handleEnded);
    };
  }, [autoPlay, onLoadedMetadata, onPlay, onPause, audioNode]);
  const play = () => {
    setIsPlaying(true);
  };
  const pause = () => {
    setIsPlaying(false);
  };
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };
  const mute = () => {
    setIsMuted(true);
    onMute();
  };
  const unmute = () => {
    setIsMuted(false);
    onUnmute();
  };
  const toggleMute = () => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  };
  const controls = {
    play,
    pause,
    togglePlay,
    mute,
    unmute,
    toggleMute
  };
  const state = {
    isPlaying,
    isMuted
  };
  return [audioCallbackRef, state, controls];
}

// src/hooks/useBoundingclientrect.ts
import { useState as useState6, useCallback as useCallback8 } from "react";

// src/hooks/useDidMount.ts
import { useEffect as useEffect9 } from "react";
function useDidMount(callback) {
  useEffect9(() => {
    if (typeof callback === "function") {
      callback();
    }
  }, []);
}

// src/hooks/useMutationObserver.ts
import { useEffect as useEffect10 } from "react";
var config = {
  attributes: true,
  characterData: true,
  childList: true,
  subtree: true
};
function useMutationObserver(ref, callback, options = config) {
  useEffect10(() => {
    if (ref.current) {
      const observer = new MutationObserver(callback);
      observer.observe(ref.current, options);
      return () => {
        observer.disconnect();
      };
    }
    return noop;
  }, [callback, options, ref]);
}

// src/hooks/useBoundingclientrect.ts
function getBoundingClientRect(element) {
  return element.getBoundingClientRect();
}
function useBoundingclientrect(ref) {
  const [domRect, setDomRect] = useState6(null);
  const update = useCallback8(() => {
    setDomRect(ref.current ? getBoundingClientRect(ref.current) : null);
  }, [ref]);
  useDidMount(() => {
    update();
  });
  useMutationObserver(ref, update);
  return domRect;
}

// src/hooks/useBoundingclientrectRef.ts
import { useState as useState8, useEffect as useEffect12, useCallback as useCallback10 } from "react";

// src/hooks/useForkRef.ts
import { useMemo as useMemo2 } from "react";
function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== void 0) {
    ref.current = value;
  }
}
function useForkRef(refA, refB) {
  return useMemo2(() => {
    if (refA === null && refB === null) {
      return null;
    }
    return (refValue) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}

// src/hooks/useMutationObserverRef.ts
import { useEffect as useEffect11, useCallback as useCallback9, useState as useState7 } from "react";
var config2 = {
  attributes: true,
  characterData: true,
  childList: true,
  subtree: true
};
function useMutationObserverRef(callback, options = config2) {
  const [node, setNode] = useState7(null);
  useEffect11(() => {
    if (node) {
      const observer = new MutationObserver(callback);
      observer.observe(node, options);
      return () => {
        observer.disconnect();
      };
    }
    return noop;
  }, [node, callback, options]);
  const ref = useCallback9((nodeElement) => {
    setNode(nodeElement);
  }, []);
  return [ref];
}

// src/hooks/useBoundingclientrectRef.ts
function getBoundingClientRect2(element) {
  return element.getBoundingClientRect();
}
function useBoundingclientrectRef() {
  const [domRect, setDomRect] = useState8(null);
  const [node, setNode] = useState8(null);
  const update = useCallback10(() => {
    setDomRect(node ? getBoundingClientRect2(node) : null);
  }, [node]);
  useEffect12(() => {
    update();
  }, [update]);
  const ref = useCallback10((nodeElement) => {
    setNode(nodeElement);
  }, []);
  const [mutationObserverRef] = useMutationObserverRef(update);
  const forkedRef = useForkRef(ref, mutationObserverRef);
  return [forkedRef, domRect, update];
}

// src/hooks/useCountdown.ts
import { useState as useState9 } from "react";

// src/hooks/useIntervalWhen.ts
import { useRef as useRef4, useEffect as useEffect13 } from "react";
function useIntervalWhen(callback, intervalDurationMs = 0, when = true, startImmediate = false) {
  const savedRefCallback = useRef4(void 0);
  useEffect13(() => {
    savedRefCallback.current = callback;
  });
  useEffect13(() => {
    if (when) {
      let internalCallback2 = function() {
        savedRefCallback.current?.();
      };
      var internalCallback = internalCallback2;
      if (startImmediate) {
        internalCallback2();
      }
      const interval = window.setInterval(internalCallback2, intervalDurationMs);
      return () => {
        window.clearInterval(interval);
      };
    }
    return noop;
  }, [when, intervalDurationMs, startImmediate]);
}

// src/hooks/useCountdown.ts
function useCountdown(endTime, options = {}) {
  const { interval = 1e3, onDown, onEnd } = options;
  const [time, setTime] = useState9(() => /* @__PURE__ */ new Date());
  const restTime = endTime.getTime() - time.getTime();
  const count = restTime > 0 ? Math.ceil(restTime / interval) : 0;
  useIntervalWhen(onTick, count ? interval : void 0, true, true);
  return count;
  function onTick() {
    const newTime = /* @__PURE__ */ new Date();
    if (newTime > endTime) {
      if (onEnd) {
        onEnd(newTime);
      }
      setTime(endTime);
      return;
    }
    if (onDown) {
      onDown(restTime, newTime);
    }
    setTime(newTime);
  }
}

// src/hooks/useCounter.ts
import { useCallback as useCallback11, useState as useState10 } from "react";
function useCounter(initialValue) {
  const [counter, setCounter] = useState10(initialValue);
  const incrementBy = useCallback11((incrAmount) => {
    setCounter((currentCounter) => currentCounter + incrAmount);
  }, []);
  const decrementBy = useCallback11(
    (decrAmount) => {
      incrementBy(-decrAmount);
    },
    [incrementBy]
  );
  const increment = useCallback11(() => {
    incrementBy(1);
  }, [incrementBy]);
  const decrement = useCallback11(() => {
    incrementBy(-1);
  }, [incrementBy]);
  const reset = useCallback11(() => {
    setCounter(initialValue);
  }, [initialValue]);
  return {
    decrement,
    decrementBy,
    increment,
    incrementBy,
    reset,
    value: counter
  };
}

// src/hooks/useDebounce.ts
import debounce from "lodash.debounce";
import { useRef as useRef5, useCallback as useCallback12 } from "react";

// src/hooks/useWillUnmount.ts
import { useEffect as useEffect14 } from "react";
function useWillUnmount(callback) {
  useEffect14(() => {
    return callback;
  }, []);
}

// src/hooks/useDebounce.ts
function useDebounce(callback, wait, options) {
  const createDebouncedCallback = useCallback12(
    (function_) => {
      return debounce(function_, wait, options);
    },
    [wait, options]
  );
  const freshCallback = useFreshRef(callback);
  function tick(...args) {
    freshCallback.current?.(...args);
  }
  const debouncedCallbackRef = useRef5(
    createDebouncedCallback(tick)
  );
  useWillUnmount(() => {
    debouncedCallbackRef.current?.cancel();
  });
  return debouncedCallbackRef.current;
}

// src/hooks/useDebounceFn.ts
import { useRef as useRef6, useCallback as useCallback13, useState as useState11 } from "react";
function useDebounceFn(func, delay, options = { leading: false, trailing: true }) {
  const { leading, trailing, maxWait } = options;
  if (!leading && !trailing) {
    throw new Error("leading and trailing cannot both be false");
  } else if (typeof maxWait !== "undefined" && maxWait < delay) {
    throw new Error("maxWait cannot be less than delay");
  }
  const funcRef = useFreshCallback(func);
  const [isTimeoutEnabled, setIsTimeoutEnabled] = useState11(false);
  const [key, setKey] = useState11(0);
  const lastExecutionTimeRef = useRef6(0);
  const argsRef = useRef6(void 0);
  const debouncedFn = useCallback13(
    (...args) => {
      argsRef.current = args;
      const overrideTimeout = typeof maxWait !== "undefined" && lastExecutionTimeRef.current && Date.now() - lastExecutionTimeRef.current > maxWait;
      if (leading) {
        if (isTimeoutEnabled && overrideTimeout) {
          setKey((prevKey) => prevKey + 1);
          lastExecutionTimeRef.current = Date.now();
          try {
            funcRef(...args);
          } catch (error) {
            console.warn(error);
          }
        } else if (overrideTimeout && !isTimeoutEnabled) {
          setIsTimeoutEnabled(true);
          lastExecutionTimeRef.current = Date.now();
          try {
            funcRef(...args);
          } catch (error) {
            console.warn(error);
          }
        } else if (isTimeoutEnabled && !overrideTimeout) {
        } else {
          setIsTimeoutEnabled(true);
          lastExecutionTimeRef.current = Date.now();
          try {
            funcRef(...args);
          } catch (error) {
            console.warn(error);
          }
        }
      }
      if (trailing) {
        if (isTimeoutEnabled) {
          setKey((prevKey) => prevKey + 1);
        } else {
          setIsTimeoutEnabled(true);
        }
      }
    },
    [maxWait, isTimeoutEnabled, leading, trailing, funcRef]
  );
  useTimeoutWhen(
    () => {
      if (typeof maxWait !== "undefined" && trailing) {
        if (!argsRef.current) return;
        lastExecutionTimeRef.current = Date.now();
        try {
          funcRef(...argsRef.current);
        } catch (error) {
          console.warn(error);
        }
      }
    },
    maxWait ?? Infinity,
    isTimeoutEnabled && typeof maxWait !== "undefined" && trailing,
    key
  );
  useTimeoutWhen(
    () => {
      if (trailing) {
        if (!argsRef.current) return;
        lastExecutionTimeRef.current = Date.now();
        try {
          funcRef(...argsRef.current);
        } catch (error) {
          console.warn(error);
        }
      }
      setIsTimeoutEnabled(false);
    },
    delay,
    isTimeoutEnabled && trailing,
    key
  );
  const freshDebouncedFn = useFreshCallback(debouncedFn);
  return [freshDebouncedFn, isTimeoutEnabled];
}

// src/hooks/useDebouncedValue.ts
import { useState as useState12 } from "react";

// src/hooks/useDidUpdate.ts
import { useEffect as useEffect15, useMemo as useMemo3, useRef as useRef7 } from "react";
function useDidUpdate(callback, conditions) {
  const hasMountedRef = useRef7(false);
  const internalConditions = useMemo3(() => {
    if (typeof conditions !== "undefined" && !Array.isArray(conditions)) {
      return [conditions];
    } else if (Array.isArray(conditions) && conditions.length === 0) {
      console.warn(
        "Using [] as the second argument makes useDidUpdate a noop. The second argument should either be `undefined` or an array of length greater than 0."
      );
    }
    return conditions;
  }, [conditions]);
  useEffect15(() => {
    if (hasMountedRef.current) {
      callback();
    }
  }, internalConditions);
  useDidMount(() => {
    hasMountedRef.current = true;
  });
  useWillUnmount(() => {
    hasMountedRef.current = false;
  });
}

// src/hooks/useDebouncedValue.ts
var defaultUseDebounceValueOptions = {
  initializeWithNull: false
};
var useDebouncedValue = (value, timeout, options = {}) => {
  const { initializeWithNull } = Object.assign(
    {},
    defaultUseDebounceValueOptions,
    options
  );
  const [updatedValue, setUpdatedValue] = useState12(
    initializeWithNull ? null : value
  );
  const debouncedSetUpdatedValue = useDebounce(setUpdatedValue, timeout);
  useDidMount(() => {
    if (initializeWithNull) {
      debouncedSetUpdatedValue(value);
    }
  });
  useDidUpdate(() => {
    debouncedSetUpdatedValue(value);
  }, [value]);
  return [updatedValue, setUpdatedValue];
};

// src/hooks/useDeepCompareEffect.ts
import { useEffect as useEffect16, useRef as useRef8 } from "react";
import isEqual from "fast-deep-equal";

// src/hooks/warning.ts
var isDevelopmentEnvironment = process.env.NODE_ENV !== "production";
var warning = () => {
};
if (isDevelopmentEnvironment) {
  const printWarning = (actualMessage) => {
    const message = `Warning: ${actualMessage}`;
    if (typeof console !== "undefined") {
      console.error(message);
    }
    try {
      throw new Error(message);
    } catch {
    }
  };
  warning = function(condition, actualMessage) {
    if (!condition) {
      printWarning(actualMessage);
    }
  };
}

// src/hooks/useDeepCompareEffect.ts
function isPrimitive(value) {
  const valueType = typeof value;
  return valueType === "string" || valueType === "number" || valueType === "bigint" || valueType === "boolean" || valueType === "undefined" || valueType === "symbol";
}
function useDeepCompareEffect(callback, dependencies) {
  const previousDeps = useRef8(dependencies);
  if (!Array.isArray(dependencies)) {
    throw new Error(
      "useDeepCompareEffect should be used with an array of dependencies"
    );
  }
  const hasPrimitives = dependencies.every(isPrimitive);
  warning(
    !hasPrimitives,
    "useDeepCompareEffect should not be used with primitive values as dependencies"
  );
  if (!isEqual(previousDeps.current, dependencies)) {
    previousDeps.current = dependencies;
  }
  useEffect16(callback, previousDeps.current);
}

// src/hooks/useDimensionsRef.ts
import { useState as useState13, useCallback as useCallback14, useLayoutEffect as useLayoutEffect2 } from "react";

// src/hooks/useGlobalObjectEventListener.ts
import { useEffect as useEffect17 } from "react";

// src/hooks/useFreshTick.ts
function useFreshTick(callback) {
  const freshRef = useFreshRef(callback);
  function tick(...args) {
    if (typeof freshRef.current === "function") {
      freshRef.current(...args);
    }
  }
  return tick;
}

// src/hooks/useGlobalObjectEventListener.ts
function useGlobalObjectEventListener(globalObject, eventName, callback, listenerOptions = {}, when = true, isLayoutEffect = false) {
  const freshCallback = useFreshTick(callback);
  const useEffectToRun = isLayoutEffect ? useIsomorphicEffect : useEffect17;
  useEffectToRun(() => {
    warning(
      typeof globalObject !== "undefined",
      "[useGlobalObjectEventListener]: Cannot attach event handlers to undefined."
    );
    if (typeof globalObject !== "undefined" && when) {
      globalObject.addEventListener(eventName, freshCallback, listenerOptions);
      return () => {
        globalObject.removeEventListener(
          eventName,
          freshCallback,
          listenerOptions
        );
      };
    }
    return () => {
    };
  }, [eventName, listenerOptions]);
}

// src/hooks/useOnWindowResize.ts
function useOnWindowResize(callback, when = true, isLayoutEffect = false) {
  useGlobalObjectEventListener(
    global.window,
    "resize",
    callback,
    { passive: true },
    when,
    isLayoutEffect
  );
}

// src/hooks/useOnWindowScroll.ts
function useOnWindowScroll(callback, when = true, isLayoutEffect = false) {
  useGlobalObjectEventListener(
    global.window,
    "scroll",
    callback,
    { passive: true },
    when,
    isLayoutEffect
  );
}

// src/hooks/useDimensionsRef.ts
var getDimensionObject = (node) => {
  const rect = node.getBoundingClientRect();
  return {
    bottom: rect.bottom,
    height: rect.height,
    left: rect.left,
    right: rect.right,
    top: rect.top,
    width: rect.width,
    x: rect.left,
    y: rect.top
  };
};
var noWindowReturnValue = [void 0, null, null];
var useDimensionsRef = ({
  updateOnScroll = true,
  updateOnResize = true
} = {}) => {
  const [dimensions, setDimensions] = useState13(null);
  const [node, setNode] = useState13(null);
  const ref = useCallback14((nodeFromCallback) => {
    setNode(nodeFromCallback);
  }, []);
  const measure = useCallback14(() => {
    window.requestAnimationFrame(() => {
      if (node) {
        setDimensions(getDimensionObject(node));
      }
    });
  }, [node]);
  useLayoutEffect2(() => {
    measure();
  }, [measure]);
  useOnWindowResize(
    () => {
      measure();
    },
    updateOnResize,
    true
  );
  useOnWindowScroll(
    () => {
      measure();
    },
    updateOnScroll,
    true
  );
  if (typeof window === "undefined") {
    console.warn("useDimensionsRef: window is undefined.");
    return noWindowReturnValue;
  }
  return [ref, dimensions, node];
};

// src/hooks/useDocumentEventListener.ts
function useDocumentEventListener(eventName, callback, listenerOptions = {}, isLayoutEffect = false) {
  useGlobalObjectEventListener(
    global.document,
    eventName,
    callback,
    listenerOptions,
    true,
    isLayoutEffect
  );
}

// src/hooks/useDocumentTitle.ts
import { useEffect as useEffect18, useRef as useRef9 } from "react";
function useDocumentTitle(title, options = {}) {
  const isBrowser = typeof window !== "undefined";
  const prevTitleRef = useRef9(isBrowser ? document.title : "");
  const { resetOnUnmount = false } = options;
  useEffect18(() => {
    if (isBrowser) {
      document.title = title;
      const lastTitle = prevTitleRef.current;
      if (resetOnUnmount) {
        return () => {
          document.title = lastTitle;
        };
      }
    }
    return noop;
  }, [title, isBrowser, resetOnUnmount]);
}

// src/hooks/useDocumentVisibilityState.ts
import { useCallback as useCallback15, useState as useState14 } from "react";
function useDocumentVisibilityState() {
  const [visibilityState, setVisibilityState] = useState14(
    document ? document.visibilityState : null
  );
  const handleVisibilityChange = useCallback15(() => {
    setVisibilityState(document ? document.visibilityState : null);
  }, []);
  useGlobalObjectEventListener(
    global.document,
    "visibilitychange",
    handleVisibilityChange,
    {},
    true,
    true
  );
  return visibilityState;
}

// src/hooks/useEffectOnceWhen.ts
import { useEffect as useEffect19, useRef as useRef10 } from "react";
function useEffectOnceWhen(callback, when = true) {
  const hasRunOnceRef = useRef10(false);
  const callbackRef = useRef10(callback);
  useEffect19(() => {
    callbackRef.current = callback;
  });
  useEffect19(() => {
    if (when && !hasRunOnceRef.current) {
      callbackRef.current();
      hasRunOnceRef.current = true;
    }
  }, [when]);
}

// src/hooks/useEventListenerRef.ts
import { useEffect as useEffect20 } from "react";

// src/hooks/useRefElement.ts
import { useCallback as useCallback16, useState as useState15 } from "react";
function useRefElement() {
  const [refElement, setRefElement] = useState15(null);
  const ref = useCallback16(
    (element) => {
      setRefElement(element);
    },
    []
  );
  return [ref, refElement];
}

// src/hooks/useEventListenerRef.ts
function useEventListenerRef(eventName, callback, listenerOptions = {}, isLayoutEffect = false) {
  const [ref, element] = useRefElement();
  const freshCallback = useFreshTick(callback);
  const useEffectToRun = isLayoutEffect ? useIsomorphicEffect : useEffect20;
  useEffectToRun(() => {
    if (!element?.addEventListener) {
      return noop;
    }
    element.addEventListener(eventName, freshCallback, listenerOptions);
    return () => {
      element.removeEventListener(eventName, freshCallback, listenerOptions);
    };
  }, [element, eventName, listenerOptions]);
  return ref;
}

// src/hooks/useFileDropRef.ts
import { useCallback as useCallback17, useState as useState16, useEffect as useEffect21 } from "react";
function useFileDropRef(options = {}, callbacks = {}) {
  const { accept, maxFileSize, maxFiles } = options;
  const {
    onDrop = noop,
    onFileAccepted = noop,
    onFileRejected = noop,
    onDragEnter = noop,
    onDragLeave = noop
  } = callbacks;
  const [targetNode, setTargetNode] = useState16(null);
  const freshOnDrop = useFreshCallback(onDrop);
  const freshOnFileAccepted = useFreshCallback(onFileAccepted);
  const freshOnFileRejected = useFreshCallback(onFileRejected);
  const freshOnDragEnter = useFreshCallback(onDragEnter);
  const freshOnDragLeave = useFreshCallback(onDragLeave);
  useCallback17((node) => {
    setTargetNode(node);
  }, []);
  const fileIsValid = useCallback17(
    (file) => {
      if (accept && !accept.includes(file.type)) {
        return { valid: false, reason: "File type not allowed" };
      }
      if (maxFileSize && file.size > maxFileSize) {
        return { valid: false, reason: "File size exceeds the limit" };
      }
      return { valid: true };
    },
    [accept, maxFileSize]
  );
  const handleDrop = useCallback17(
    (event) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files || []);
      const acceptedFiles = [];
      const rejectedFiles = [];
      if (maxFiles && files.length > maxFiles) {
        for (const file of files) {
          freshOnFileRejected(file, "Exceeded maximum number of files");
        }
      } else {
        files.forEach((file) => {
          const validationResult = fileIsValid(file);
          if (validationResult.valid) {
            acceptedFiles.push(file);
            freshOnFileAccepted(file);
          } else {
            rejectedFiles.push(file);
            freshOnFileRejected(
              file,
              validationResult.reason || "Unknown reason"
            );
          }
        });
      }
      freshOnDrop(acceptedFiles, rejectedFiles);
    },
    [
      fileIsValid,
      freshOnFileAccepted,
      freshOnFileRejected,
      maxFiles,
      freshOnDrop
    ]
  );
  const handleDragOver = useCallback17((event) => {
    event.preventDefault();
  }, []);
  useEffect21(() => {
    if (targetNode) {
      targetNode.addEventListener("drop", handleDrop);
      targetNode.addEventListener("dragover", handleDragOver);
      targetNode.addEventListener("dragenter", freshOnDragEnter);
      targetNode.addEventListener("dragleave", freshOnDragLeave);
      return () => {
        targetNode.removeEventListener("drop", handleDrop);
        targetNode.removeEventListener("dragover", handleDragOver);
        targetNode.removeEventListener("dragenter", freshOnDragEnter);
        targetNode.removeEventListener("dragleave", freshOnDragLeave);
      };
    } else {
      return () => {
      };
    }
  }, [
    targetNode,
    handleDrop,
    handleDragOver,
    freshOnDragEnter,
    freshOnDragLeave
  ]);
  return useCallback17((node) => {
    setTargetNode(node);
  }, []);
}

// src/hooks/useFullscreen.ts
import { useState as useState17, useCallback as useCallback18, useEffect as useEffect22 } from "react";
var FullscreenApi = {
  DOM_UNAVAILABLE_ERROR: "DOM is unavailable server-side. Please use this method client-side only.",
  FULLSCREEN_UNSUPPORTED_ERROR: "Your browser does not support Fullscreen API.",
  getEventsNames() {
    if (typeof document === "undefined") return null;
    const _document = document;
    if ("exitFullscreen" in _document)
      return ["fullscreenchange", "fullscreenerror"];
    if ("webkitExitFullscreen" in _document)
      return ["webkitfullscreenchange", "webkitfullscreenerror"];
    if ("webkitCancelFullScreen" in _document)
      return ["webkitfullscreenchange", "webkitfullscreenerror"];
    if ("mozCancelFullScreen" in _document)
      return ["mozfullscreenchange", "mozfullscreenerror"];
    if ("msExitFullscreen" in _document)
      return ["MSFullscreenChange", "MSFullscreenError"];
    return null;
  },
  getEventName(eventType) {
    const eventsNames = this.getEventsNames();
    if (!eventsNames) return null;
    if (eventType === "change") return eventsNames[0];
    return eventsNames[1];
  },
  get fullscreenEnabled() {
    if (typeof document === "undefined") return false;
    const _document = document;
    return _document.fullscreenEnabled || _document.webkitFullscreenEnabled || !!_document.webkitCancelFullScreen || _document.mozFullScreenEnabled || _document.msFullscreenEnabled || false;
  },
  get fullscreenElement() {
    if (typeof document === "undefined") return null;
    const _document = document;
    return _document.fullscreenElement || _document.webkitFullscreenElement || _document.webkitCurrentFullScreenElement || _document.mozFullScreenElement || _document.msFullscreenElement || null;
  },
  requestFullscreen(element, options) {
    if (typeof document === "undefined")
      throw new Error(this.DOM_UNAVAILABLE_ERROR);
    const target = element ?? document.documentElement;
    const method = target.requestFullscreen || target.webkitRequestFullscreen || target.webkitRequestFullScreen || target.mozRequestFullScreen || target.msRequestFullscreen;
    if (!method) throw new Error(this.FULLSCREEN_UNSUPPORTED_ERROR);
    return method.call(target, options);
  },
  exitFullscreen() {
    if (typeof document === "undefined")
      throw new Error(this.DOM_UNAVAILABLE_ERROR);
    const _document = document;
    const method = _document.exitFullscreen || _document.webkitExitFullscreen || _document.webkitCancelFullScreen || _document.mozCancelFullScreen || _document.msExitFullscreen;
    if (!method) throw new Error(this.FULLSCREEN_UNSUPPORTED_ERROR);
    return method.call(_document);
  },
  on(eventType, callback) {
    const eventName = this.getEventName(eventType);
    if (!eventName) return;
    document.addEventListener(eventName, callback);
  },
  off(eventType, callback) {
    const eventName = this.getEventName(eventType);
    if (!eventName) return;
    document.removeEventListener(eventName, callback);
  }
};
function useFullscreen(props = {}) {
  const { target, onChange, onError, requestFullScreenOptions } = props;
  const [isFullscreenAvailable, setIsFullscreenAvailable] = useState17(false);
  const [fullscreenElement, setFullscreenElement] = useState17(
    null
  );
  const [isFullscreenEnabled, setIsFullscreenEnabled] = useState17(false);
  const enableFullscreen = useCallback18(() => {
    return FullscreenApi.requestFullscreen(
      target?.current || null,
      requestFullScreenOptions
    );
  }, [target, requestFullScreenOptions]);
  const disableFullscreen = useCallback18(() => {
    return FullscreenApi.exitFullscreen();
  }, []);
  const toggleFullscreen = useCallback18(() => {
    if (!!FullscreenApi.fullscreenElement) return disableFullscreen();
    return enableFullscreen();
  }, [enableFullscreen, disableFullscreen]);
  const onChangeHandler = useCallback18(
    (event) => {
      const fullscreenElement2 = FullscreenApi.fullscreenElement;
      setFullscreenElement(fullscreenElement2);
      setIsFullscreenEnabled(!!fullscreenElement2);
      onChange?.(event);
    },
    [onChange]
  );
  const onErrorHandler = useCallback18(
    (event) => {
      const fullscreenElement2 = FullscreenApi.fullscreenElement;
      setFullscreenElement(fullscreenElement2);
      setIsFullscreenEnabled(!!fullscreenElement2);
      onError?.(event);
    },
    [onError]
  );
  useEffect22(() => {
    setIsFullscreenAvailable(FullscreenApi.fullscreenEnabled);
  }, []);
  useEffect22(() => {
    FullscreenApi.on("change", onChangeHandler);
    FullscreenApi.on("error", onErrorHandler);
    return () => {
      FullscreenApi.off("change", onChangeHandler);
      FullscreenApi.off("error", onErrorHandler);
    };
  }, [onChangeHandler, onErrorHandler]);
  return {
    isFullscreenAvailable,
    fullscreenElement,
    isFullscreenEnabled,
    enableFullscreen,
    disableFullscreen,
    toggleFullscreen
  };
}

// src/hooks/useFocus.ts
import { useCallback as useCallback19 } from "react";
var useFocus = (props) => {
  const {
    onBlur: propsOnBlur,
    onFocus: propsOnFocus,
    onFocusChange: propsOnFocusChange
  } = props;
  const onBlur = useCallback19(
    (e) => {
      if (e.target === e.currentTarget) {
        if (propsOnBlur) propsOnBlur(e);
        if (propsOnFocusChange) propsOnFocusChange(false);
      }
    },
    [propsOnBlur, propsOnFocusChange]
  );
  const onFocus = useCallback19(
    (e) => {
      if (e.target === e.currentTarget) {
        if (propsOnFocus) propsOnFocus(e);
        if (propsOnFocusChange) propsOnFocusChange(true);
      }
    },
    [propsOnFocusChange, propsOnFocus]
  );
  return {
    focusProps: {
      onFocus,
      onBlur
    }
  };
};

// src/hooks/useFocusWithin.ts
import { useCallback as useCallback20, useRef as useRef11 } from "react";
var useFocusWithin = (props) => {
  const { onBlurWithin, onFocusWithin, onFocusWithinChange } = props;
  const state = useRef11({
    isFocusWithin: false
  });
  const onBlur = useCallback20(
    (e) => {
      if (state.current.isFocusWithin && !e.currentTarget.contains(e.relatedTarget)) {
        state.current.isFocusWithin = false;
        if (onBlurWithin) onBlurWithin(e);
        if (onFocusWithinChange) onFocusWithinChange(false);
      }
    },
    [onBlurWithin, onFocusWithinChange]
  );
  const onFocus = useCallback20(
    (e) => {
      if (!state.current.isFocusWithin) {
        if (onFocusWithin) onFocusWithin(e);
        if (onFocusWithinChange) onFocusWithinChange(true);
        state.current.isFocusWithin = true;
      }
    },
    [onFocusWithin, onFocusWithinChange]
  );
  return {
    focusWithinProps: {
      onFocus,
      onBlur
    }
  };
};

// src/hooks/useGeolocation.ts
import { useEffect as useEffect23, useState as useState18 } from "react";
function getGeoLocation(options) {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { coords } = position;
          const { latitude, longitude } = coords;
          resolve({
            isError: false,
            lat: latitude,
            lng: longitude,
            message: ""
          });
        },
        (error) => {
          resolve({
            isError: true,
            message: error && typeof error === "object" && "message" in error ? error.message : "Geolocation error"
          });
        },
        options
      );
    } else {
      resolve({
        isError: true,
        message: "Geolocation is not supported for this Browser/OS."
      });
    }
  });
}
var defaultGeoLocationOptions = {
  enableHighAccuracy: false,
  maximumAge: 0,
  timeout: Number.POSITIVE_INFINITY,
  when: true
};
var useGeolocation = (geoLocationOptions = defaultGeoLocationOptions) => {
  const [geoObject, setGeoObject] = useState18(
    null
  );
  const { when, enableHighAccuracy, timeout, maximumAge } = geoLocationOptions;
  const getIsMounted = useGetIsMounted();
  useEffect23(() => {
    async function getGeoCode() {
      try {
        const value = await getGeoLocation({
          enableHighAccuracy,
          maximumAge,
          timeout,
          when
        });
        if (getIsMounted()) {
          setGeoObject(value);
        }
      } catch (error) {
        if (getIsMounted()) {
          setGeoObject({
            isError: true,
            message: error instanceof Error ? error.message : String(error)
          });
        }
      }
    }
    if (when) {
      void getGeoCode();
    }
  }, [when, enableHighAccuracy, timeout, maximumAge, getIsMounted]);
  return geoObject;
};

// src/hooks/useInput.ts
import { useState as useState19, useEffect as useEffect24, useCallback as useCallback21 } from "react";
var defaultOptions = {};
function useInput(initialValue = "", options = defaultOptions) {
  const [value, setValue] = useState19(initialValue);
  const onChange = useCallback21(
    (event) => {
      const newValue = event.target.value;
      let shouldUpdate = true;
      if (typeof options.validate === "function") {
        shouldUpdate = options.validate(newValue, value);
      }
      if (shouldUpdate) {
        setValue(newValue);
      }
    },
    [options, value]
  );
  useEffect24(() => {
    setValue(initialValue);
  }, [initialValue]);
  const handler = {
    onChange,
    value
  };
  return handler;
}

// src/hooks/useIntersectionObserverRef.ts
import { useEffect as useEffect25, useCallback as useCallback22, useState as useState20, useRef as useRef12 } from "react";
var config3 = {
  root: null,
  rootMargin: "0px 0px 0px 0px",
  threshold: [0, 1]
};
function useIntersectionObserverRef(callback, options = config3) {
  const { root = null, rootMargin, threshold } = options;
  const [node, setNode] = useState20(null);
  const callbackRef = useRef12(callback);
  useEffect25(() => {
    callbackRef.current = callback;
  });
  const handleIntersectionObserver = useCallback22(
    (...args) => {
      return callbackRef.current?.(...args);
    },
    []
  );
  useEffect25(() => {
    if (node) {
      const observer = new IntersectionObserver(handleIntersectionObserver, {
        root,
        rootMargin,
        threshold
      });
      observer.observe(node);
      return () => {
        observer.disconnect();
      };
    }
    return noop;
  }, [node, handleIntersectionObserver, root, rootMargin, threshold]);
  const ref = useCallback22((nodeElement) => {
    setNode(nodeElement);
  }, []);
  return [ref];
}

// src/hooks/useInViewRef.ts
import { useEffect as useEffect26, useCallback as useCallback23, useState as useState21 } from "react";
var config4 = {
  root: null,
  rootMargin: "0px 0px 0px 0px",
  threshold: [0, 1]
};
function useInViewRef(callbackOrOptions, options) {
  const callback = typeof callbackOrOptions === "function" ? callbackOrOptions : noop;
  const opts = typeof callbackOrOptions === "object" ? callbackOrOptions : options || config4;
  const { root = null, rootMargin, threshold } = opts;
  const [node, setNode] = useState21(null);
  const [inView, setInView] = useState21(false);
  useEffect26(() => {
    if (node) {
      const observer = new IntersectionObserver((entries, observerRef) => {
        for (const { isIntersecting } of entries) setInView(isIntersecting);
        callback(entries, observerRef);
      }, opts);
      observer.observe(node);
      return () => {
        observer.disconnect();
      };
    }
    return noop;
  }, [node, callback, root, rootMargin, threshold, opts]);
  const ref = useCallback23((nodeElement) => {
    setNode(nodeElement);
  }, []);
  return [ref, inView];
}

// src/hooks/useIsDroppingFiles.ts
import { useState as useState22, useEffect as useEffect27, useCallback as useCallback24 } from "react";
function useIsDroppingFiles(isTargetWindow = false) {
  const [targetNode, setTargetNode] = useState22(null);
  const ref = useCallback24((node) => {
    setTargetNode(node);
  }, []);
  const [isDroppingFiles, setIsDroppingFiles] = useState22(false);
  const handleDragEnter = useCallback24(
    (e) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        if (!isDroppingFiles) {
          setIsDroppingFiles(true);
        }
      } else {
        setIsDroppingFiles(false);
      }
    },
    [isDroppingFiles]
  );
  const handleDragOver = useCallback24(
    (e) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        if (!isDroppingFiles) {
          setIsDroppingFiles(true);
        }
      } else {
        setIsDroppingFiles(false);
      }
    },
    [isDroppingFiles]
  );
  const handleDragLeave = useCallback24((e) => {
    e.preventDefault();
    setIsDroppingFiles(false);
  }, []);
  const handleDrop = useCallback24((e) => {
    e.preventDefault();
    setIsDroppingFiles(false);
  }, []);
  const freshHandleDragEnter = useFreshCallback(handleDragEnter);
  const freshHandleDragOver = useFreshCallback(handleDragOver);
  const freshHandleDragLeave = useFreshCallback(handleDragLeave);
  const freshHandleDrop = useFreshCallback(handleDrop);
  useEffect27(() => {
    const target = isTargetWindow ? window : targetNode;
    if (target) {
      target.addEventListener("dragenter", freshHandleDragEnter);
      target.addEventListener("dragover", freshHandleDragOver);
      target.addEventListener("dragleave", freshHandleDragLeave);
      target.addEventListener("drop", freshHandleDrop);
    }
    return () => {
      if (target) {
        target.removeEventListener("dragenter", freshHandleDragEnter);
        target.removeEventListener("dragover", freshHandleDragOver);
        target.removeEventListener("dragleave", freshHandleDragLeave);
        target.removeEventListener("drop", freshHandleDrop);
      }
    };
  }, [
    freshHandleDragEnter,
    freshHandleDragLeave,
    freshHandleDragOver,
    freshHandleDrop,
    isDroppingFiles,
    isTargetWindow,
    targetNode
  ]);
  if (isTargetWindow) {
    return isDroppingFiles;
  }
  return [ref, isDroppingFiles];
}

// src/hooks/useKey.ts
import { useEffect as useEffect28, useCallback as useCallback25, useRef as useRef13, useMemo as useMemo4 } from "react";

// src/utils/doesIdentifierMatchKeyboardEvent.ts
var doesIdentifierMatchKeyboardEvent = (error, identifier) => {
  if (error.key === identifier || error.code === identifier || error.keyCode === identifier || error.which === identifier || error.charCode === identifier) {
    return true;
  }
  return false;
};

// src/hooks/useKey.ts
var defaultOptions2 = {
  eventTypes: ["keydown"],
  when: true
};
function useKey(keys, callback, options) {
  const keyList = useMemo4(() => {
    if (Array.isArray(keys)) {
      return keys;
    } else {
      return [keys];
    }
  }, [keys]);
  const internalOptions = useMemo4(() => {
    return { ...defaultOptions2, ...options };
  }, [options]);
  const { when, eventTypes } = internalOptions;
  const callbackRef = useRef13(callback);
  const { target } = internalOptions;
  useEffect28(() => {
    callbackRef.current = callback;
  });
  const handle = useCallback25(
    (event) => {
      if (keyList.some(
        (identifier) => doesIdentifierMatchKeyboardEvent(event, identifier)
      )) {
        callbackRef.current(event);
      }
    },
    [keyList]
  );
  useEffect28(() => {
    if (when && typeof window !== "undefined") {
      if (target) {
        const targetNode = target.current;
        if (targetNode) {
          for (const eventType of eventTypes) {
            targetNode.addEventListener(eventType, handle);
          }
          return () => {
            for (const eventType of eventTypes) {
              targetNode.removeEventListener(eventType, handle);
            }
          };
        }
      } else {
        for (const eventType of eventTypes) {
          window.addEventListener(eventType, handle);
        }
        return () => {
          for (const eventType of eventTypes) {
            window.removeEventListener(eventType, handle);
          }
        };
      }
    }
    return noop;
  }, [when, eventTypes, keyList, target, callback, handle]);
}

// src/hooks/useKeyBindings.ts
var useKeyBindings = (keyBindings, options) => {
  for (const key in keyBindings) {
    const handler = keyBindings[key];
    if (handler) {
      useKey(key, handler, options);
    }
  }
};

// src/hooks/useKeyRef.ts
import { useEffect as useEffect29, useState as useState23, useCallback as useCallback26, useRef as useRef14, useMemo as useMemo5 } from "react";
var defaultOptions3 = {
  eventTypes: ["keydown"],
  when: true
};
function useKeyRef(keys, callback, options) {
  const [targetNode, setTargetNode] = useState23(null);
  const ref = useCallback26((node) => {
    setTargetNode(node);
  }, []);
  const keyList = useMemo5(() => {
    if (Array.isArray(keys)) {
      return keys;
    } else {
      return [keys];
    }
  }, [keys]);
  const internalOptions = useMemo5(() => {
    return { ...defaultOptions3, ...options };
  }, [options]);
  const { when, eventTypes } = internalOptions;
  const callbackRef = useRef14(callback);
  useEffect29(() => {
    callbackRef.current = callback;
  });
  const handle = useCallback26(
    (event) => {
      if (keyList.some(
        (identifier) => doesIdentifierMatchKeyboardEvent(event, identifier)
      )) {
        callbackRef.current(event);
      }
    },
    [keyList]
  );
  useEffect29(() => {
    if (when && targetNode) {
      for (const eventType of eventTypes) {
        targetNode.addEventListener(eventType, handle);
      }
      return () => {
        for (const eventType of eventTypes) {
          targetNode.removeEventListener(eventType, handle);
        }
      };
    }
    return noop;
  }, [targetNode, when, eventTypes, keyList, handle]);
  return ref;
}

// src/hooks/useKeys.ts
import { useEffect as useEffect30, useRef as useRef15, useCallback as useCallback27 } from "react";
var defaultOptions4 = {
  continuous: false,
  when: true
};
function useKeys(keysList, callback, options) {
  const internalOptions = { ...defaultOptions4, ...options };
  const { target, when, continuous, preventLostKeyup } = internalOptions;
  const savedCallback = useRef15(callback);
  const pressedKeyMappingRef = useRef15({});
  const PressedKeyMapping = pressedKeyMappingRef.current;
  useEffect30(() => {
    savedCallback.current = callback;
  });
  const handleKeyDown = useCallback27(
    (event) => {
      let pressedKeyIdentifier = null;
      let areAllKeysFromListPressed = false;
      for (const identifier of keysList) {
        if (doesIdentifierMatchKeyboardEvent(event, identifier)) {
          PressedKeyMapping[identifier] = true;
          pressedKeyIdentifier = identifier;
        }
      }
      if (keysList.every((identifier) => Boolean(PressedKeyMapping[identifier]))) {
        areAllKeysFromListPressed = true;
      }
      if (areAllKeysFromListPressed) {
        savedCallback.current(event);
        if (!continuous && pressedKeyIdentifier !== null) {
          PressedKeyMapping[pressedKeyIdentifier] = false;
        }
      }
    },
    [keysList, PressedKeyMapping, continuous]
  );
  const handleKeyUp = useCallback27(
    (event) => {
      for (const identifier of keysList) {
        if (doesIdentifierMatchKeyboardEvent(event, identifier)) {
          PressedKeyMapping[identifier] = void 0;
        }
      }
    },
    [PressedKeyMapping, keysList]
  );
  useEffect30(() => {
    if (when && typeof window !== "undefined") {
      const targetNode = target?.current ? target.current : document;
      targetNode.addEventListener("keydown", handleKeyDown);
      targetNode.addEventListener("keyup", handleKeyUp);
      return () => {
        targetNode.removeEventListener("keydown", handleKeyDown);
        targetNode.removeEventListener("keyup", handleKeyUp);
      };
    }
    return noop;
  }, [when, target, keysList, handleKeyDown, handleKeyUp]);
  useEffect30(() => {
    if (preventLostKeyup !== true) return noop;
    if (typeof window !== "undefined") {
      const originalAlert = window.alert;
      window.alert = (message) => {
        for (const identifier of keysList) {
          PressedKeyMapping[identifier] = void 0;
        }
        return originalAlert(message);
      };
      const originalConfirm = window.confirm;
      window.confirm = (message) => {
        for (const identifier of keysList) {
          PressedKeyMapping[identifier] = void 0;
        }
        return originalConfirm(message);
      };
      const originalPrompt = window.prompt;
      window.prompt = (message, _default) => {
        for (const identifier of keysList) {
          PressedKeyMapping[identifier] = void 0;
        }
        return originalPrompt(message, _default);
      };
      return () => {
        window.alert = originalAlert;
        window.confirm = originalConfirm;
        window.prompt = originalPrompt;
      };
    }
    return noop;
  }, [PressedKeyMapping, keysList, preventLostKeyup]);
}

// src/hooks/useLifecycleLogger.ts
var useLifecycleLogger = (componentName = "Component", ...otherArgs) => {
  useDidMount(() => {
    console.log(`${componentName} mounted`, ...otherArgs);
    return () => console.log(`${componentName} unmounted`);
  });
  useDidUpdate(() => {
    console.log(`${componentName} updated`, ...otherArgs);
  });
  useWillUnmount(() => {
    console.log(`${componentName} unmounted`);
  });
};

// src/hooks/useLockBodyScroll.ts
import { useEffect as useEffect31 } from "react";
function useLockBodyScroll(isLocked) {
  useEffect31(() => {
    if (isLocked) {
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    } else {
      return noop;
    }
  }, [isLocked]);
}

// src/hooks/useLocalstorageState.ts
import { useMemo as useMemo6, useState as useState24, useEffect as useEffect32, useCallback as useCallback28, useRef as useRef16 } from "react";
function getValueFromLocalStorage(key) {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const storedValue = localStorage.getItem(key) ?? "null";
  try {
    return JSON.parse(storedValue);
  } catch (error) {
    console.error(error);
  }
  return storedValue;
}
function saveValueToLocalStorage(key, value) {
  if (typeof localStorage === "undefined") {
    return null;
  }
  if (value === void 0) {
    return localStorage.removeItem(key);
  }
  return localStorage.setItem(key, JSON.stringify(value));
}
function initialize(key, initialState) {
  const valueLoadedFromLocalStorage = getValueFromLocalStorage(key);
  if (valueLoadedFromLocalStorage === null) {
    return typeof initialState === "function" ? initialState() : initialState;
  } else {
    return valueLoadedFromLocalStorage;
  }
}
function useLocalstorageState(key, initialState) {
  const [value, setValue] = useState24(() => initialize(key, initialState));
  const isUpdateFromCrossDocumentListener = useRef16(false);
  const isUpdateFromWithinDocumentListener = useRef16(false);
  const customEventTypeName = useMemo6(() => {
    return `rooks-${key}-localstorage-update`;
  }, [key]);
  useEffect32(() => {
    if (!isUpdateFromCrossDocumentListener.current || !isUpdateFromWithinDocumentListener.current) {
      saveValueToLocalStorage(key, value);
    }
  }, [key, value]);
  const listenToCrossDocumentStorageEvents = useCallback28(
    (event) => {
      if (event.storageArea === localStorage && event.key === key) {
        try {
          isUpdateFromCrossDocumentListener.current = true;
          const newValue = JSON.parse(event.newValue ?? "null");
          if (value !== newValue) {
            setValue(newValue);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
    [key, value]
  );
  useEffect32(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("storage", listenToCrossDocumentStorageEvents);
      return () => {
        window.removeEventListener(
          "storage",
          listenToCrossDocumentStorageEvents
        );
      };
    } else {
      console.warn("useLocalstorageState: window is undefined.");
      return () => {
      };
    }
  }, [listenToCrossDocumentStorageEvents]);
  const listenToCustomEventWithinDocument = useCallback28(
    (event) => {
      try {
        isUpdateFromWithinDocumentListener.current = true;
        const { newValue } = event.detail;
        if (value !== newValue) {
          setValue(newValue);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [value]
  );
  useEffect32(() => {
    if (typeof document !== "undefined") {
      document.addEventListener(
        customEventTypeName,
        listenToCustomEventWithinDocument
      );
      return () => {
        document.removeEventListener(
          customEventTypeName,
          listenToCustomEventWithinDocument
        );
      };
    } else {
      console.warn("[useLocalstorageState] document is undefined.");
      return () => {
      };
    }
  }, [customEventTypeName, listenToCustomEventWithinDocument]);
  const broadcastValueWithinDocument = useCallback28(
    (newValue) => {
      if (typeof document !== "undefined") {
        const event = new CustomEvent(
          customEventTypeName,
          { detail: { newValue } }
        );
        document.dispatchEvent(event);
      } else {
        console.warn("[useLocalstorageState] document is undefined.");
      }
    },
    [customEventTypeName]
  );
  const currentValue = useFreshRef(value, true);
  const set = useCallback28(
    (newValue) => {
      const resolvedNewValue = typeof newValue === "function" ? newValue(currentValue.current) : newValue;
      isUpdateFromCrossDocumentListener.current = false;
      isUpdateFromWithinDocumentListener.current = false;
      setValue(resolvedNewValue);
      broadcastValueWithinDocument(resolvedNewValue);
    },
    [broadcastValueWithinDocument, currentValue]
  );
  const remove = useCallback28(() => {
    localStorage.removeItem(key);
  }, [key]);
  return [value, set, remove];
}

// src/hooks/useNativeMapState.ts
import { useState as useState25, useCallback as useCallback29 } from "react";
function useNativeMapState(initialMapState) {
  const [map, setMap] = useState25(initialMapState || /* @__PURE__ */ new Map());
  const set = useCallback29((key, value) => {
    setMap((prevMap) => new Map(prevMap.set(key, value)));
  }, []);
  const setMultiple = useCallback29((entries) => {
    setMap((prevMap) => {
      const newMap = new Map(prevMap);
      entries.forEach(([key, value]) => {
        newMap.set(key, value);
      });
      return newMap;
    });
  }, []);
  const remove = useCallback29((key) => {
    setMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.delete(key);
      return newMap;
    });
  }, []);
  const removeMultiple = useCallback29((keys) => {
    setMap((prevMap) => {
      const newMap = new Map(prevMap);
      keys.forEach((key) => {
        newMap.delete(key);
      });
      return newMap;
    });
  }, []);
  const clear = useCallback29(() => {
    setMap(/* @__PURE__ */ new Map());
  }, []);
  const has = useCallback29(
    (key) => {
      return map.has(key);
    },
    [map]
  );
  const hasSome = useCallback29(
    (keys) => {
      return keys.some((key) => map.has(key));
    },
    [map]
  );
  const hasEvery = useCallback29(
    (keys) => {
      return keys.every((key) => map.has(key));
    },
    [map]
  );
  const get = useCallback29(
    (key) => {
      return map.get(key);
    },
    [map]
  );
  const size = useCallback29(() => {
    return map.size;
  }, [map]);
  const controls = {
    set,
    remove,
    clear,
    has,
    get,
    size,
    setMultiple,
    removeMultiple,
    hasSome,
    hasEvery
  };
  return [map, controls];
}

// src/hooks/useMediaMatch.ts
import { useEffect as useEffect33, useMemo as useMemo7, useState as useState26 } from "react";
function useMediaMatch(query) {
  const matchMedia = useMemo7(
    () => window.matchMedia(query),
    [query]
  );
  const [matches, setMatches] = useState26(() => matchMedia.matches);
  useEffect33(() => {
    setMatches(matchMedia.matches);
    const listener = (event) => setMatches(event.matches);
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener("change", listener);
      return () => matchMedia.removeEventListener("change", listener);
    } else {
      matchMedia.addListener(listener);
      return () => matchMedia.removeListener(listener);
    }
  }, [matchMedia]);
  if (typeof window === "undefined") {
    console.warn("useMediaMatch cannot function as window is undefined.");
    return false;
  }
  return matches;
}

// src/hooks/useMergeRefs.ts
import { useMemo as useMemo8 } from "react";
function setRef2(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== void 0) {
    ref.current = value;
  }
}
function useMergeRefs(...refs) {
  return useMemo8(() => {
    if (refs.every((ref) => ref === null)) {
      return null;
    }
    return (refValue) => {
      for (const ref of refs) {
        setRef2(ref, refValue);
      }
    };
  }, [refs]);
}

// src/hooks/useMouse.ts
import { useEffect as useEffect34, useState as useState27 } from "react";
var initialMouseState = {
  clientX: null,
  clientY: null,
  movementX: null,
  movementY: null,
  offsetX: null,
  offsetY: null,
  pageX: null,
  pageY: null,
  screenX: null,
  screenY: null,
  x: null,
  y: null
};
function getMousePositionFromEvent(event) {
  const {
    screenX,
    screenY,
    movementX,
    movementY,
    pageX,
    pageY,
    clientX,
    clientY,
    offsetX,
    offsetY
  } = event;
  return {
    clientX,
    clientY,
    movementX,
    movementY,
    offsetX,
    offsetY,
    pageX,
    pageY,
    screenX,
    screenY,
    x: screenX,
    y: screenY
  };
}
function useMouse() {
  const [mousePosition, setMousePosition] = useState27(initialMouseState);
  function updateMousePosition(event) {
    setMousePosition(getMousePositionFromEvent(event));
  }
  useEffect34(() => {
    document.addEventListener("mousemove", updateMousePosition);
    return () => {
      document.removeEventListener("mousemove", updateMousePosition);
    };
  }, []);
  return mousePosition;
}

// src/hooks/useMouseMoveDelta.ts
import { useState as useState28, useEffect as useEffect35, useCallback as useCallback30, useRef as useRef17, useMemo as useMemo9 } from "react";
var initialDelta = {
  deltaX: 0,
  deltaY: 0,
  clientX: null,
  clientY: null,
  velocityX: 0,
  velocityY: 0,
  timeStamp: Date.now()
};
function useMouseMoveDelta() {
  const [delta, setDelta] = useState28(() => {
    return {
      ...initialDelta,
      timeStamp: Date.now()
    };
  });
  const lastDeltaRef = useRef17(null);
  const handleMouseMove = useCallback30(
    (event) => {
      const currentTimestamp = event.timeStamp;
      const lastDelta = lastDeltaRef.current;
      if (lastDelta && lastDelta.clientX !== null && lastDelta.clientY !== null) {
        const deltaX = event.clientX - lastDelta.clientX;
        const deltaY = event.clientY - lastDelta.clientY;
        const timeDelta = currentTimestamp - lastDelta.timeStamp;
        const velocityX = timeDelta === 0 ? 0 : deltaX / timeDelta;
        const velocityY = timeDelta === 0 ? 0 : deltaY / timeDelta;
        lastDeltaRef.current = delta;
        setDelta({
          deltaX,
          clientX: event.clientX,
          clientY: event.clientY,
          deltaY,
          velocityX,
          velocityY,
          timeStamp: currentTimestamp
        });
      } else {
        lastDeltaRef.current = {
          ...delta,
          clientX: event.clientX,
          clientY: event.clientY,
          timeStamp: event.timeStamp
        };
        setDelta({
          clientX: event.clientX,
          clientY: event.clientY,
          deltaX: 0,
          deltaY: 0,
          velocityX: 0,
          velocityY: 0,
          timeStamp: event.timeStamp
        });
      }
    },
    [delta]
  );
  const freshMouseMove = useFreshCallback(handleMouseMove);
  useEffect35(() => {
    document.addEventListener("mousemove", freshMouseMove, {
      passive: true
    });
    return () => {
      document.removeEventListener("mousemove", freshMouseMove);
    };
  }, [freshMouseMove]);
  return useMemo9(() => {
    const { deltaX, deltaY, velocityX, velocityY } = delta;
    return { deltaX, deltaY, velocityX, velocityY };
  }, [delta]);
}

// src/hooks/useMouseWheelDelta.ts
import { useState as useState29, useEffect as useEffect36, useCallback as useCallback31, useRef as useRef18, useMemo as useMemo10 } from "react";
var initialDelta2 = {
  delta: 0,
  velocity: 0,
  timeStamp: Date.now()
};
function useMouseWheelDelta() {
  const [deltaState, setDeltaState] = useState29(() => {
    return {
      ...initialDelta2,
      timeStamp: Date.now()
    };
  });
  const lastDeltaRef = useRef18(null);
  const handleWheel = useCallback31(
    (event) => {
      const currentTimestamp = event.timeStamp;
      const lastDelta = lastDeltaRef.current;
      if (lastDelta) {
        const delta = event.deltaY;
        const timeDelta = currentTimestamp - lastDelta.timeStamp;
        console.log(timeDelta);
        const velocity = timeDelta === 0 ? 0 : delta / timeDelta;
        console.log("delta", delta);
        console.log("timedelta", timeDelta);
        console.log("velocity", velocity);
        lastDeltaRef.current = deltaState;
        setDeltaState({
          delta,
          velocity,
          timeStamp: currentTimestamp
        });
      } else {
        lastDeltaRef.current = {
          velocity: 0,
          delta: 0,
          timeStamp: event.timeStamp
        };
        setDeltaState({
          delta: event.deltaY,
          velocity: 0,
          timeStamp: event.timeStamp
        });
      }
    },
    [deltaState]
  );
  const freshWheel = useFreshCallback(handleWheel);
  useEffect36(() => {
    document.addEventListener("wheel", freshWheel);
    return () => {
      document.removeEventListener("wheel", freshWheel);
    };
  }, [freshWheel]);
  console.log(deltaState);
  return useMemo10(() => {
    const { delta, velocity } = deltaState;
    return { delta, velocity };
  }, [deltaState]);
}

// src/hooks/useMultiSelectableList.ts
import { useCallback as useCallback32, useState as useState30 } from "react";
function warnIfBothValueAndIndexAreProvided(functionName, object) {
  if (Object.values(object).every((value) => typeof value !== "undefined")) {
    console.warn(
      `${functionName}. Expected either ${Object.keys(object).join(
        " or "
      )} to be provided. However all were provided`
    );
  } else if (Object.values(object).every((value) => typeof value === "undefined")) {
    console.warn(
      `${functionName}. ${Object.keys(object).join(" , ")} are all undefined.`
    );
  }
}
function useMultiSelectableList(list = [], initialSelectIndices = [0], allowUnselected = false) {
  const [currentIndices, setCurrentIndices] = useState30(initialSelectIndices);
  const currentValues = currentIndices.map((index) => list[index]).filter((value) => value !== void 0);
  const selection = [currentIndices, currentValues];
  const updateSelections = ({ indices, values }) => {
    return () => {
      warnIfBothValueAndIndexAreProvided("updateSelections", {
        indices,
        values
      });
      if (typeof indices !== "undefined") {
        if (!allowUnselected && indices.length === 0) {
          console.warn(`updateSelections failed. indices is an empty list.`);
          return;
        }
        setCurrentIndices(indices);
      } else if (typeof values !== "undefined") {
        const valueIndices = list.reduce((accumulator, current, index) => {
          if (values.includes(current)) {
            const array = [...accumulator, index];
            return array;
          }
          return accumulator;
        }, []);
        if (valueIndices.length > 0) {
          setCurrentIndices(valueIndices);
        } else if (allowUnselected) {
          setCurrentIndices(valueIndices);
        } else {
          console.warn(
            `updateSelections failed. Do the values exist in the list?`
          );
        }
      }
    };
  };
  const toggleSelectionByIndex = useCallback32(
    (index) => {
      let newIndices;
      if (!currentIndices.includes(index)) {
        newIndices = [...currentIndices, index];
      } else {
        newIndices = [...currentIndices];
        const indexOfIndex = currentIndices.indexOf(index);
        if (indexOfIndex !== -1) {
          newIndices.splice(indexOfIndex, 1);
        }
      }
      if (newIndices.length > 0) {
        setCurrentIndices(newIndices);
      } else if (allowUnselected) {
        setCurrentIndices(newIndices);
      } else {
        console.warn(
          `toggleSelection failed. Do the values exist in the list?`
        );
      }
    },
    [allowUnselected, currentIndices]
  );
  const toggleSelection = useCallback32(
    ({ index, value }) => {
      return () => {
        warnIfBothValueAndIndexAreProvided("toggleSelection", {
          index,
          value
        });
        if (typeof index !== "undefined") {
          toggleSelectionByIndex(index);
        } else if (typeof value !== "undefined") {
          const valueIndex = list.indexOf(value);
          if (valueIndex > -1) {
            toggleSelectionByIndex(valueIndex);
          }
        }
      };
    },
    [list, toggleSelectionByIndex]
  );
  const matchSelection = useCallback32(
    ({ index, value }) => {
      warnIfBothValueAndIndexAreProvided("matchSelection", { index, value });
      if (typeof index !== "undefined") {
        return currentIndices.includes(index);
      } else if (typeof value !== "undefined") {
        return currentValues.includes(value);
      }
      return false;
    },
    [currentIndices, currentValues]
  );
  const controls = {
    matchSelection,
    toggleSelection,
    updateSelections
  };
  return [selection, controls];
}

// src/hooks/useNavigatorLanguage.ts
import { useState as useState31 } from "react";

// src/hooks/useWindowEventListener.ts
function useWindowEventListener(eventName, callback, listenerOptions = {}, isLayoutEffect = false) {
  useGlobalObjectEventListener(
    global.window,
    eventName,
    callback,
    listenerOptions,
    true,
    isLayoutEffect
  );
}

// src/hooks/useNavigatorLanguage.ts
function getLanguage() {
  if (typeof navigator !== "undefined") {
    return navigator.language;
  } else {
    return null;
  }
}
function useNavigatorLanguage() {
  const [language, setLanguage] = useState31(getLanguage);
  useWindowEventListener("languagechange", () => {
    setLanguage(getLanguage);
  });
  return language;
}

// src/hooks/useOnClickRef.ts
import { useState as useState32, useEffect as useEffect37, useCallback as useCallback33 } from "react";
function useOnClickRef(onClick) {
  const [node, setNode] = useState32(null);
  const handleEvent = useCallback33(
    (event) => {
      event.preventDefault();
      onClick();
    },
    [onClick]
  );
  const freshCallback = useFreshCallback(handleEvent);
  useEffect37(() => {
    if (node) {
      node.addEventListener("click", freshCallback);
      node.addEventListener("touchend", freshCallback);
      return () => {
        node.removeEventListener("click", freshCallback);
        node.removeEventListener("touchend", freshCallback);
      };
    }
    return noop;
  }, [node, freshCallback]);
  const ref = useCallback33((element) => {
    if (element) {
      setNode(element);
    }
  }, []);
  return ref;
}

// src/hooks/useOnHoverRef.ts
import { useState as useState33, useEffect as useEffect38, useCallback as useCallback34 } from "react";
function useOnHoverRef(onMouseEnter, onMouseLeave) {
  const [node, setNode] = useState33(null);
  const handleMouseEnter = useCallback34(
    (e) => {
      onMouseEnter?.(e);
    },
    [onMouseEnter]
  );
  const handleMouseLeave = useCallback34(
    (e) => {
      onMouseLeave?.(e);
    },
    [onMouseLeave]
  );
  const mouseEnterCallback = useFreshCallback(handleMouseEnter);
  const mouseLeaveCallback = useFreshCallback(handleMouseLeave);
  useEffect38(() => {
    if (node) {
      node.addEventListener("mouseenter", mouseEnterCallback);
      node.addEventListener("mouseleave", mouseLeaveCallback);
      return () => {
        node.removeEventListener("mouseenter", mouseEnterCallback);
        node.removeEventListener("mouseleave", mouseLeaveCallback);
      };
    } else {
      return noop;
    }
  }, [mouseEnterCallback, mouseLeaveCallback, node]);
  const ref = (el) => {
    setNode(el);
  };
  return ref;
}

// src/hooks/useOnline.ts
import { useSyncExternalStore } from "use-sync-external-store/shim";
function getSnapshot() {
  if (typeof window === "undefined") {
    return null;
  }
  return navigator.onLine;
}
function subscribe(onStoreChange) {
  if (typeof window !== "undefined") {
    window.addEventListener("online", onStoreChange);
    window.addEventListener("offline", onStoreChange);
    return () => {
      window.removeEventListener("online", onStoreChange);
      window.removeEventListener("offline", onStoreChange);
    };
  } else {
    console.warn("useOnline: window is undefined.");
    return noop;
  }
}
function useOnline() {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot);
  return isOnline;
}

// src/hooks/useOrientation.ts
import { useState as useState34, useEffect as useEffect39 } from "react";
var useOrientation = () => {
  const [orientation, setOrientation] = useState34(
    typeof window !== "undefined" ? window.screen.orientation : null
  );
  useEffect39(() => {
    const handleOrientationChange = () => {
      setOrientation(window.screen.orientation);
    };
    window.screen.orientation.addEventListener(
      "change",
      handleOrientationChange
    );
    return () => {
      window.screen.orientation.removeEventListener(
        "change",
        handleOrientationChange
      );
    };
  }, []);
  return orientation;
};

// src/hooks/useOutsideClick.ts
import { useEffect as useEffect40, useRef as useRef19, useCallback as useCallback35 } from "react";
function useOutsideClick(ref, handler, when = true) {
  const savedHandler = useRef19(handler);
  const memoizedCallback = useCallback35(
    (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        savedHandler.current(event);
      }
    },
    [ref]
  );
  useEffect40(() => {
    savedHandler.current = handler;
  });
  useEffect40(() => {
    if (when) {
      document.addEventListener("click", memoizedCallback, true);
      document.addEventListener("touchstart", memoizedCallback, true);
      return () => {
        document.removeEventListener("click", memoizedCallback, true);
        document.removeEventListener("touchstart", memoizedCallback, true);
      };
    }
    return noop;
  }, [ref, handler, when, memoizedCallback]);
}

// src/hooks/useOutsideClickRef.ts
import { useEffect as useEffect41, useRef as useRef20, useCallback as useCallback36, useState as useState35 } from "react";
function useOutsideClickRef(handler, when = true) {
  const [node, setNode] = useState35(null);
  const savedHandler = useRef20(handler);
  const memoizedCallback = useCallback36(
    (event) => {
      if (node && !node.contains(event.target)) {
        savedHandler.current(event);
      }
    },
    [node]
  );
  useEffect41(() => {
    savedHandler.current = handler;
  });
  const ref = useCallback36((nodeElement) => {
    setNode(nodeElement);
  }, []);
  useEffect41(() => {
    if (when) {
      document.addEventListener("click", memoizedCallback, true);
      document.addEventListener("touchstart", memoizedCallback, true);
      return () => {
        document.removeEventListener("click", memoizedCallback, true);
        document.removeEventListener("touchstart", memoizedCallback, true);
      };
    }
    return noop;
  }, [when, memoizedCallback]);
  return [ref];
}

// src/hooks/usePreviousDifferent.ts
import { useRef as useRef21, useEffect as useEffect42 } from "react";
function usePreviousDifferent(currentValue) {
  const previousRef = useRef21(null);
  const previousRef2 = useRef21(null);
  useEffect42(() => {
    previousRef2.current = previousRef.current;
    previousRef.current = currentValue;
  }, [currentValue]);
  return currentValue === previousRef.current ? previousRef2.current : previousRef.current;
}

// src/hooks/usePreviousImmediate.ts
import { useRef as useRef22, useEffect as useEffect43 } from "react";
function usePreviousImmediate(currentValue) {
  const previousRef = useRef22(null);
  useEffect43(() => {
    previousRef.current = currentValue;
  });
  return previousRef.current;
}

// src/hooks/usePromise.ts
import { useState as useState36, useEffect as useEffect44 } from "react";
function usePromise(asyncFunction, deps = []) {
  const [state, setState] = useState36({
    data: null,
    loading: true,
    error: null
  });
  const freshAsyncCallback = useFreshCallback(asyncFunction);
  useEffect44(() => {
    let isMounted = true;
    async function fetchData() {
      try {
        const data = await freshAsyncCallback();
        if (isMounted) {
          setState({
            data,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            data: null,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error))
          });
        }
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [freshAsyncCallback, ...deps]);
  return state;
}

// src/hooks/useQueueState.ts
import { useCallback as useCallback37, useState as useState37 } from "react";
function useQueueState(initialList) {
  const [list, setList] = useState37([...initialList]);
  const enqueue = useCallback37(
    (item) => {
      const newList = [...list, item];
      setList(newList);
      return newList.length;
    },
    [list]
  );
  const dequeue = useCallback37(() => {
    if (list.length > 0) {
      const firstItem = list[0];
      setList([...list.slice(1)]);
      return firstItem;
    }
    return void 0;
  }, [list]);
  const peek = useCallback37(() => {
    if (list.length > 0) {
      return list[0];
    }
    return void 0;
  }, [list]);
  const controls = {
    dequeue,
    enqueue,
    length: list.length,
    peek
  };
  return [list, controls];
}

// src/hooks/useRaf.ts
import raf from "raf";
import { useRef as useRef23, useEffect as useEffect45 } from "react";
function useRaf(callback, isActive) {
  const savedCallback = useRef23(null);
  useEffect45(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect45(() => {
    let animationFrame;
    let startTime = Date.now();
    function tick() {
      const timeElapsed = Date.now() - startTime;
      startTime = Date.now();
      loop();
      savedCallback.current?.(timeElapsed);
    }
    function loop() {
      animationFrame = raf(tick);
    }
    if (isActive) {
      startTime = Date.now();
      loop();
      return () => {
        if (animationFrame) {
          raf.cancel(animationFrame);
        }
      };
    } else {
      return noop;
    }
  }, [isActive]);
}

// src/hooks/useResizeObserverRef.ts
import { useCallback as useCallback38, useEffect as useEffect46, useRef as useRef24, useState as useState38 } from "react";
var config5 = {
  box: "content-box"
};
function useResizeObserverRef(callback, options = config5) {
  const { box } = options;
  const [node, setNode] = useState38(null);
  const callbackRef = useRef24(callback);
  useEffect46(() => {
    callbackRef.current = callback;
  });
  const handleResizeObserver = useCallback38(
    (...args) => {
      callbackRef.current?.(...args);
    },
    []
  );
  useEffect46(() => {
    if (node) {
      const observer = new ResizeObserver(handleResizeObserver);
      observer.observe(node, { box });
      return () => {
        observer.disconnect();
      };
    }
    return noop;
  }, [node, handleResizeObserver, box]);
  const ref = useCallback38((node2) => {
    setNode(node2);
  }, []);
  return [ref];
}

// src/hooks/useRenderCount.ts
import { useRef as useRef25 } from "react";
function useRenderCount() {
  return ++useRef25(0).current;
}

// src/hooks/useSafeSetState.ts
import { useState as useState39, useCallback as useCallback39 } from "react";
function useSafeSetState(initialState) {
  const [state, setState] = useState39(initialState);
  const getIsMounted = useGetIsMounted();
  const safeSetState = useCallback39(
    (newState) => {
      if (getIsMounted()) {
        setState(newState);
      }
    },
    [getIsMounted]
  );
  return [state, safeSetState];
}

// src/hooks/useSelect.ts
import { useCallback as useCallback40, useState as useState40 } from "react";
function useSelect(list, initialIndex = 0) {
  const [selectedIndex, setSelectedIndex] = useState40(initialIndex);
  const setItem = useCallback40(
    (item) => {
      setSelectedIndex(list.indexOf(item));
    },
    [list]
  );
  return {
    index: selectedIndex,
    item: list[selectedIndex],
    setIndex: setSelectedIndex,
    setItem
  };
}

// src/hooks/useSelectableList.ts
import { useCallback as useCallback41, useState as useState41 } from "react";
function warnIfBothValueAndIndexAreProvided2(functionName, object) {
  if (Object.values(object).every((value) => typeof value !== "undefined")) {
    console.warn(
      `${functionName}. Expected either ${Object.keys(object).join(
        " or "
      )} to be provided. However all were provided`
    );
  } else if (Object.values(object).every((value) => typeof value === "undefined")) {
    console.warn(
      `${functionName}. ${Object.keys(object).join(" , ")} are all undefined.`
    );
  }
}
function useSelectableList(list = [], initialIndex = 0, allowUnselected = false) {
  const [currentIndex, setCurrentIndex] = useState41(initialIndex);
  const currentValue = list[currentIndex];
  const selection = [currentIndex, currentValue];
  const updateSelection = useCallback41(
    ({ index, value }) => {
      return () => {
        warnIfBothValueAndIndexAreProvided2("updateSelection", { index, value });
        if (typeof index !== "undefined") {
          setCurrentIndex(index);
        } else if (typeof value !== "undefined") {
          const valueIndex = list.indexOf(value);
          if (valueIndex > -1) {
            setCurrentIndex(valueIndex);
          } else {
            console.warn(
              `updateSelection failed. Does the value ${value} exist in the list?`
            );
          }
        }
      };
    },
    [list]
  );
  const toggleSelection = useCallback41(
    ({ index, value }) => {
      return () => {
        warnIfBothValueAndIndexAreProvided2("toggleSelection", { index, value });
        if (typeof index !== "undefined") {
          if (currentIndex === index) {
            if (allowUnselected) {
              setCurrentIndex(-1);
            } else {
              console.warn("allowUnselected is false. Cannot unselect item");
            }
          } else {
            setCurrentIndex(index);
          }
        } else if (typeof value !== "undefined") {
          const valueIndex = list.indexOf(value);
          if (valueIndex > -1) {
            if (currentIndex === valueIndex) {
              if (allowUnselected) {
                setCurrentIndex(-1);
              } else {
                console.warn("allowUnselected is false. Cannot unselect item");
              }
            } else {
              setCurrentIndex(valueIndex);
            }
          } else {
            console.log("as");
            console.warn(
              `toggleSelection failed. Does the value ${value} exist in the list?`
            );
          }
        }
      };
    },
    [allowUnselected, currentIndex, list]
  );
  const matchSelection = useCallback41(
    ({ index, value }) => {
      warnIfBothValueAndIndexAreProvided2("matchSelection", { index, value });
      if (typeof index !== "undefined") {
        return index === currentIndex;
      } else {
        return value === currentValue;
      }
    },
    [currentIndex, currentValue]
  );
  const controls = {
    matchSelection,
    toggleSelection,
    updateSelection
  };
  return [selection, controls];
}

// src/hooks/useSessionstorageState.ts
import { useMemo as useMemo11, useState as useState42, useEffect as useEffect47, useCallback as useCallback42, useRef as useRef26 } from "react";
function getValueFromSessionStorage(key) {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  const storedValue = sessionStorage.getItem(key) ?? "null";
  try {
    return JSON.parse(storedValue);
  } catch (error) {
    console.error(error);
  }
  return storedValue;
}
function saveValueToSessionStorage(key, value) {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  return sessionStorage.setItem(key, JSON.stringify(value));
}
function initialize2(key, initialState) {
  const valueLoadedFromSessionStorage = getValueFromSessionStorage(key);
  if (valueLoadedFromSessionStorage === null) {
    return initialState;
  } else {
    return valueLoadedFromSessionStorage;
  }
}
function useSessionstorageState(key, initialState) {
  const [value, setValue] = useState42(() => initialize2(key, initialState));
  const isUpdateFromCrossDocumentListener = useRef26(false);
  const isUpdateFromWithinDocumentListener = useRef26(false);
  const customEventTypeName = useMemo11(() => {
    return `rooks-${key}-sessionstorage-update`;
  }, [key]);
  useEffect47(() => {
    if (!isUpdateFromCrossDocumentListener.current) {
      saveValueToSessionStorage(key, value);
    }
  }, [key, value]);
  const listenToCrossDocumentStorageEvents = useCallback42(
    (event) => {
      if (event.storageArea === sessionStorage && event.key === key) {
        try {
          isUpdateFromCrossDocumentListener.current = true;
          const newValue = JSON.parse(event.newValue ?? "null");
          if (value !== newValue) {
            setValue(newValue);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
    [key, value]
  );
  useEffect47(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("storage", listenToCrossDocumentStorageEvents);
      return () => {
        window.removeEventListener(
          "storage",
          listenToCrossDocumentStorageEvents
        );
      };
    } else {
      console.warn("[useSessionstorageState] window is undefined.");
      return () => {
      };
    }
  }, [listenToCrossDocumentStorageEvents]);
  const listenToCustomEventWithinDocument = useCallback42(
    (event) => {
      try {
        isUpdateFromWithinDocumentListener.current = true;
        const { newValue } = event.detail;
        if (value !== newValue) {
          setValue(newValue);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [value]
  );
  useEffect47(() => {
    if (typeof document !== "undefined") {
      document.addEventListener(
        customEventTypeName,
        listenToCustomEventWithinDocument
      );
      return () => {
        document.removeEventListener(
          customEventTypeName,
          listenToCustomEventWithinDocument
        );
      };
    } else {
      console.warn("[useSessionstorageState] document is undefined.");
      return () => {
      };
    }
  }, [customEventTypeName, listenToCustomEventWithinDocument]);
  const broadcastValueWithinDocument = useCallback42(
    (newValue) => {
      if (typeof document !== "undefined") {
        const event = new CustomEvent(
          customEventTypeName,
          { detail: { newValue } }
        );
        document.dispatchEvent(event);
      } else {
        console.warn("[useSessionstorageState] document is undefined.");
      }
    },
    [customEventTypeName]
  );
  const set = useCallback42(
    (newValue) => {
      const resolvedNewValue = typeof newValue === "function" ? newValue(value) : newValue;
      isUpdateFromCrossDocumentListener.current = false;
      isUpdateFromWithinDocumentListener.current = false;
      setValue(resolvedNewValue);
      broadcastValueWithinDocument(resolvedNewValue);
    },
    [broadcastValueWithinDocument, value]
  );
  const remove = useCallback42(() => {
    sessionStorage.removeItem(key);
  }, [key]);
  return [value, set, remove];
}

// src/hooks/useSetState.ts
import { useCallback as useCallback43, useMemo as useMemo12, useState as useState43 } from "react";
function useSetState(initialSetValue) {
  const [setValue, setSetValue] = useState43(new Set(initialSetValue));
  const add = useCallback43(
    (...args) => {
      setSetValue(new Set(setValue.add(...args)));
    },
    [setValue, setSetValue]
  );
  const deleteValue = useCallback43(
    (...args) => {
      const newSetValue = new Set(setValue);
      newSetValue.delete(...args);
      setSetValue(newSetValue);
    },
    [setValue, setSetValue]
  );
  const clear = useCallback43(() => {
    setSetValue(/* @__PURE__ */ new Set());
  }, [setSetValue]);
  const controls = useMemo12(() => {
    return { add, delete: deleteValue, clear };
  }, [add, deleteValue, clear]);
  const returnValue = useMemo12(() => {
    return [setValue, controls];
  }, [setValue, controls]);
  return returnValue;
}

// src/hooks/useStackState.ts
import { useCallback as useCallback44, useMemo as useMemo13, useState as useState44 } from "react";
function useStackState(initialList) {
  const [list, setList] = useState44([...initialList]);
  const length = list.length;
  const listInReverse = useMemo13(() => {
    const reverseList = [...list];
    reverseList.reverse();
    return reverseList;
  }, [list]);
  const push = useCallback44(
    (item) => {
      const newList = [...list, item];
      setList(newList);
      return newList.length;
    },
    [list]
  );
  const pop = useCallback44(() => {
    if (list.length > 0) {
      const lastItem = list[list.length - 1];
      setList([...list.slice(0, list.length - 1)]);
      return lastItem;
    }
    return void 0;
  }, [list]);
  const peek = useCallback44(() => {
    if (list.length > 0) {
      return list[list.length - 1];
    }
    return void 0;
  }, [list]);
  const clear = () => setList([]);
  const isEmpty = useCallback44(() => list.length === 0, [list]);
  const controls = {
    clear,
    isEmpty,
    length,
    peek,
    pop,
    push
  };
  return [list, controls, listInReverse];
}

// src/hooks/useSpeech.ts
import { useRef as useRef27, useCallback as useCallback45, useState as useState45, useEffect as useEffect48 } from "react";
function useSpeech(options) {
  const {
    text,
    language = "en-US",
    voiceURI,
    onEnd = noop,
    volume = 1,
    pitch = 1,
    rate = 1
  } = options;
  const freshOnEnd = useFreshCallback(onEnd);
  const speechRef = useRef27(null);
  const [isPlaying, setIsPlaying] = useState45(false);
  useEffect48(() => {
    if (speechRef.current && isPlaying) {
      speechRef.current.text = text;
      speechRef.current.lang = language;
      speechRef.current.volume = volume;
      speechRef.current.pitch = pitch;
      speechRef.current.rate = rate;
      if (voiceURI) {
        const voices = window.speechSynthesis.getVoices();
        const selectedVoice = voices.find(
          (voice) => voice.voiceURI === voiceURI
        );
        if (selectedVoice) {
          speechRef.current.voice = selectedVoice;
        }
      }
    }
  }, [text, isPlaying, language, voiceURI, volume, pitch, rate]);
  const start = useCallback45(() => {
    if (!("speechSynthesis" in window)) {
      console.error("Web Speech API is not supported in your browser.");
      return;
    }
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
    speechRef.current = new SpeechSynthesisUtterance(text);
    speechRef.current.lang = language;
    speechRef.current.volume = volume;
    speechRef.current.pitch = pitch;
    speechRef.current.rate = rate;
    if (voiceURI) {
      const voices = window.speechSynthesis.getVoices();
      const selectedVoice = voices.find((voice) => voice.voiceURI === voiceURI);
      if (selectedVoice) {
        speechRef.current.voice = selectedVoice;
      }
    }
    speechRef.current.onstart = () => setIsPlaying(true);
    speechRef.current.onend = () => {
      setIsPlaying(false);
      freshOnEnd();
    };
    speechRef.current.onerror = () => setIsPlaying(false);
    window.speechSynthesis.speak(speechRef.current);
  }, [text, language, volume, pitch, rate, voiceURI, freshOnEnd]);
  const pause = useCallback45(() => {
    if (window.speechSynthesis && isPlaying) {
      window.speechSynthesis.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);
  const resume = useCallback45(() => {
    if (window.speechSynthesis && !isPlaying) {
      window.speechSynthesis.resume();
      setIsPlaying(true);
    }
  }, [isPlaying]);
  const stop = useCallback45(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  }, []);
  return {
    start,
    pause,
    resume,
    stop,
    isPlaying
  };
}

// src/utils/utils.ts
var isFunction = (value) => typeof value === "function";
var isNumber = (value) => typeof value === "number";

// src/hooks/useTimeTravelState.ts
import { useCallback as useCallback46, useMemo as useMemo14, useRef as useRef28, useState as useState46 } from "react";
var getSplitIndex = (step, arr) => {
  let index = step > 0 ? step - 1 : arr.length + step;
  if (index >= arr.length - 1) {
    index = arr.length - 1;
  }
  if (index < 0) {
    index = 0;
  }
  return index;
};
var split = (step, targetArr) => {
  const index = getSplitIndex(step, targetArr);
  return {
    _current: targetArr[index],
    _before: targetArr.slice(0, index),
    _after: targetArr.slice(index + 1)
  };
};
function useTimeTravelState(initialValue) {
  const [history, setHistory] = useState46({
    present: initialValue,
    past: [],
    future: []
  });
  const { present, past, future } = history;
  const initialValueRef = useRef28(initialValue);
  const reset = useCallback46(
    (resetInitialValue) => {
      const newInitialValue = resetInitialValue ?? initialValueRef.current;
      initialValueRef.current = newInitialValue;
      setHistory({
        present: newInitialValue,
        future: [],
        past: []
      });
    },
    [initialValueRef, setHistory]
  );
  const updateValue = useCallback46((val, options) => {
    setHistory((currentHistory) => {
      const { past: past2, present: present2 } = currentHistory;
      const { overwriteLastEntry = false } = options || {};
      let newValue = void 0;
      if (isFunction(val)) {
        newValue = val(present2);
      } else {
        newValue = val;
      }
      if (overwriteLastEntry) {
        return {
          present: newValue,
          past: past2,
          future: []
        };
      } else {
        return {
          present: newValue,
          future: [],
          past: [...past2, present2]
        };
      }
    });
  }, []);
  const goForwardInternal = useCallback46((step = 1) => {
    setHistory((currentHistory) => {
      const { future: future2, past: past2, present: present2 } = currentHistory;
      if (future2.length === 0) {
        return currentHistory;
      }
      const { _before, _current, _after } = split(step, future2);
      return {
        past: [...past2, present2, ..._before],
        present: _current,
        future: _after
      };
    });
  }, []);
  const goBackwardInternal = useCallback46((step = -1) => {
    setHistory((currentHistory) => {
      const { future: future2, past: past2, present: present2 } = currentHistory;
      if (past2.length === 0) {
        return currentHistory;
      }
      const { _before, _current, _after } = split(step, past2);
      return {
        past: _before,
        present: _current,
        future: [..._after, present2, ...future2]
      };
    });
  }, []);
  const go = useCallback46(
    (step) => {
      const stepNum = isNumber(step) ? step : Number(step);
      if (stepNum === 0) {
        return;
      }
      if (stepNum > 0) {
        return goForwardInternal(stepNum);
      }
      goBackwardInternal(stepNum);
    },
    [goBackwardInternal, goForwardInternal]
  );
  const back = useCallback46(() => {
    go(-1);
  }, [go]);
  const forward = useCallback46(() => {
    go(1);
  }, [go]);
  const canUndo = useMemo14(() => {
    return past.length > 0;
  }, [past.length]);
  const canRedo = useMemo14(() => {
    return future.length > 0;
  }, [future.length]);
  const controls = useMemo14(() => {
    return {
      backLength: past.length,
      forwardLength: future.length,
      go,
      back,
      forward,
      reset,
      undo: back,
      redo: forward,
      canUndo,
      canRedo
    };
  }, [back, canRedo, canUndo, forward, future.length, go, past.length, reset]);
  const returnValue = useMemo14(() => {
    return [present, updateValue, controls];
  }, [controls, present, updateValue]);
  return returnValue;
}

// src/hooks/useThrottle.ts
import { useState as useState47, useEffect as useEffect49, useCallback as useCallback47, useRef as useRef29 } from "react";
function useThrottle(callback, timeout = 300) {
  const [ready, setReady] = useState47(true);
  const timerRef = useRef29(void 0);
  const throttledFunction = useCallback47(
    (...args) => {
      if (!ready) {
        return;
      }
      setReady(false);
      callback(...args);
    },
    [ready, callback]
  );
  useEffect49(() => {
    if (!ready) {
      timerRef.current = window.setTimeout(() => {
        setReady(true);
      }, timeout);
      return () => window.clearTimeout(timerRef.current);
    }
    return noop;
  }, [ready, timeout]);
  return [throttledFunction, ready];
}

// src/hooks/useToggle.ts
import { useReducer } from "react";
var defaultToggleFunction = (value) => !value;
function useToggle(initialValue = false, toggleFunction = defaultToggleFunction) {
  return useReducer(toggleFunction, initialValue);
}

// src/hooks/useUndoState.ts
import { useCallback as useCallback48, useMemo as useMemo15, useState as useState48 } from "react";
var defaultOptions5 = { maxSize: 100 };
var useUndoState = (defaultValue, options) => {
  const { maxSize } = useMemo15(() => {
    return { ...defaultOptions5, ...options };
  }, [options]);
  const [value, setValue] = useState48([defaultValue]);
  const push = useCallback48(
    (argument) => {
      return setValue((current) => {
        const restValues = current.length >= maxSize ? current.slice(0, maxSize) : current;
        if (typeof argument === "function") {
          return [argument(current[0]), ...restValues];
        } else {
          return [argument, ...restValues];
        }
      });
    },
    [maxSize]
  );
  const undo = useCallback48(() => {
    setValue((current) => {
      if (current.length === 1) {
        return current;
      }
      const [, ...values] = current;
      return values;
    });
  }, []);
  const returnValue = useMemo15(() => {
    return [value[0], push, undo];
  }, [push, undo, value]);
  return returnValue;
};

// src/hooks/useUndoRedoState.ts
import {
  useState as useState49,
  useCallback as useCallback49,
  useMemo as useMemo16
} from "react";
function isFunctionInitializer(functionToCheck) {
  return typeof functionToCheck === "function";
}
function useUndoRedoState(initialState, options) {
  const [state, _setState] = useState49(initialState);
  const [past, setPast] = useState49([]);
  const [future, setFuture] = useState49([]);
  const maxDepth = options?.maxDepth ?? 100;
  const undo = useCallback49(() => {
    setPast((pastStates) => {
      const newPast = [...pastStates];
      const previousState = newPast.pop();
      if (typeof previousState !== "undefined") {
        setFuture((futureStates) => [state, ...futureStates]);
        _setState(previousState);
      }
      return newPast;
    });
  }, [state]);
  const redo = useCallback49(() => {
    setFuture((futureStates) => {
      const newFuture = [...futureStates];
      const nextState = newFuture.shift();
      if (typeof nextState !== "undefined") {
        setPast((pastStates) => [...pastStates, state]);
        _setState(nextState);
      }
      return newFuture;
    });
  }, [state]);
  const canUndo = useCallback49(() => past.length > 0, [past]);
  const canRedo = useCallback49(() => future.length > 0, [future]);
  const isUndoPossible = useMemo16(() => past.length > 0, [past]);
  const isRedoPossible = useMemo16(() => future.length > 0, [future]);
  const setState = useCallback49(
    (value) => {
      _setState((prevState) => {
        const nextState = isFunctionInitializer(value) ? value(prevState) : value;
        setPast((pastStates) => {
          const newPast = [...pastStates, prevState];
          if (newPast.length > maxDepth) {
            newPast.shift();
          }
          return newPast;
        });
        setFuture([]);
        return nextState;
      });
    },
    [maxDepth]
  );
  const clearUndoStack = useCallback49(() => {
    setPast([]);
  }, []);
  const clearRedoStack = useCallback49(() => {
    setFuture([]);
  }, []);
  const clearAll = useCallback49(() => {
    setPast([]);
    setFuture([]);
  }, []);
  const controls = useMemo16(() => {
    return {
      undo,
      redo,
      canUndo,
      canRedo,
      clearUndoStack,
      clearRedoStack,
      clearAll,
      isUndoPossible,
      isRedoPossible
    };
  }, [
    undo,
    redo,
    canUndo,
    canRedo,
    clearUndoStack,
    clearRedoStack,
    clearAll,
    isUndoPossible,
    isRedoPossible
  ]);
  return [state, setState, controls];
}

// src/hooks/useVibrate.ts
import { useEffect as useEffect50 } from "react";
function useVibrate({ isEnabled, pattern }) {
  useEffect50(() => {
    if (!("vibrate" in navigator) || typeof navigator.vibrate !== "function") {
      console.warn("Vibration API not supported by the current browser");
      return;
    }
    if (isEnabled) {
      navigator.vibrate(pattern);
    } else {
      navigator.vibrate(0);
    }
    return () => {
      navigator.vibrate(0);
    };
  }, [isEnabled, pattern]);
}

// src/hooks/useVideo.ts
import { useRef as useRef30, useState as useState50, useEffect as useEffect51 } from "react";
var useVideo = () => {
  const videoRef = useRef30(null);
  const [state, setState] = useState50({
    currentTime: 0,
    duration: 0,
    isPaused: true,
    isMuted: false,
    volume: 1
  });
  const controls = {
    play: () => {
      if (videoRef.current) {
        videoRef.current.play();
        setState({ ...state, isPaused: false });
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
        setState({ ...state, isPaused: true });
      }
    },
    toggleMute: () => {
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted;
        setState({ ...state, isMuted: videoRef.current.muted });
      }
    },
    setVolume: (volume) => {
      if (videoRef.current) {
        videoRef.current.volume = volume;
        setState({ ...state, volume });
      }
    },
    setCurrentTime: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
        setState({ ...state, currentTime: time });
      }
    },
    fastForward: (seconds) => {
      if (videoRef.current) {
        videoRef.current.currentTime += seconds;
        setState({ ...state, currentTime: videoRef.current.currentTime });
      }
    },
    rewind: (seconds) => {
      if (videoRef.current) {
        videoRef.current.currentTime -= seconds;
        setState({ ...state, currentTime: videoRef.current.currentTime });
      }
    },
    toggleFullScreen: () => {
      if (videoRef.current) {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          videoRef.current.requestFullscreen();
        }
      }
    }
  };
  useEffect51(() => {
    const video = videoRef.current;
    const handleTimeUpdate = () => {
      setState((prevState) => ({
        ...prevState,
        currentTime: video.currentTime
      }));
    };
    const handleDurationChange = () => {
      setState((prevState) => ({
        ...prevState,
        duration: video.duration
      }));
    };
    const handlePause = () => {
      setState((prevState) => ({
        ...prevState,
        isPaused: true
      }));
    };
    const handlePlay = () => {
      setState((prevState) => ({
        ...prevState,
        isPaused: false
      }));
    };
    if (video) {
      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("durationchange", handleDurationChange);
      video.addEventListener("pause", handlePause);
      video.addEventListener("play", handlePlay);
    }
    return () => {
      if (video) {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("durationchange", handleDurationChange);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("play", handlePlay);
      }
    };
  }, [videoRef]);
  return [videoRef, state, controls];
};

// src/hooks/useWhyDidYouUpdate.ts
import { useEffect as useEffect52, useRef as useRef31 } from "react";
function useWhyDidYouUpdate(componentName, currentProps, enableLogging = true) {
  const previousProps = useRef31({});
  useDidUpdate(() => {
    if (previousProps.current && enableLogging) {
      const combinedKeys = Object.keys({
        ...previousProps.current,
        ...currentProps
      });
      const changedProps = {};
      combinedKeys.forEach((key) => {
        if (!Object.is(previousProps.current[key], currentProps[key])) {
          changedProps[key] = {
            from: previousProps.current[key],
            to: currentProps[key]
          };
        }
      });
      if (Object.keys(changedProps).length) {
        console.log("[why-did-you-update]", componentName, changedProps);
      }
    }
  }, [currentProps, componentName, enableLogging]);
  useEffect52(() => {
    previousProps.current = currentProps;
  });
}

// src/hooks/useWindowScrollPosition.ts
import { useState as useState51 } from "react";
function computeScrollPosition() {
  if (typeof window === "undefined") {
    return {
      scrollX: 0,
      scrollY: 0
    };
  } else {
    return {
      scrollX: window.scrollX || window.pageXOffset,
      scrollY: window.scrollY || window.pageYOffset
    };
  }
}
function useWindowScrollPosition() {
  const [scrollPosition, setScrollPosition] = useState51(
    computeScrollPosition
  );
  useOnWindowScroll(
    () => {
      setScrollPosition(computeScrollPosition());
    },
    true,
    true
  );
  useOnWindowResize(
    () => {
      setScrollPosition(computeScrollPosition());
    },
    true,
    true
  );
  return scrollPosition;
}

// src/hooks/useWindowSize.ts
import { useState as useState52 } from "react";
var nullDimensions = {
  innerHeight: null,
  innerWidth: null,
  outerHeight: null,
  outerWidth: null
};
function getDimensions() {
  return {
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    outerHeight: window.outerHeight,
    outerWidth: window.outerWidth
  };
}
function useWindowSize() {
  const [windowSize, setWindowSize] = useState52(() => {
    if (typeof window === "undefined") {
      return nullDimensions;
    } else {
      return getDimensions();
    }
  });
  function onResize() {
    setWindowSize(getDimensions());
  }
  useIsomorphicEffect(() => {
    if (typeof window === "undefined") {
      return () => {
      };
    } else {
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }
  }, []);
  return windowSize;
}
export {
  useArrayState,
  useAsyncEffect,
  useAudio,
  useBoundingclientrect,
  useBoundingclientrectRef,
  useCountdown,
  useCounter,
  useDebounce,
  useDebounceFn,
  useDebouncedValue,
  useDeepCompareEffect,
  useDidMount,
  useDidUpdate,
  useDimensionsRef,
  useDocumentEventListener,
  useDocumentTitle,
  useDocumentVisibilityState,
  useEffectOnceWhen,
  useEventListenerRef,
  useFileDropRef,
  useFocus,
  useFocusWithin,
  useForkRef,
  useFreshCallback,
  useFreshRef,
  useFreshTick,
  useFullscreen,
  useGeolocation,
  useGetIsMounted,
  useInViewRef,
  useInput,
  useIntersectionObserverRef,
  useIntervalWhen,
  useIsDroppingFiles,
  useIsomorphicEffect,
  useKey,
  useKeyBindings,
  useKeyRef,
  useKeys,
  useLifecycleLogger,
  useLocalstorageState,
  useLockBodyScroll,
  useMapState,
  useMediaMatch,
  useMergeRefs,
  useMouse,
  useMouseMoveDelta,
  useMouseWheelDelta,
  useMultiSelectableList,
  useMutationObserver,
  useMutationObserverRef,
  useNativeMapState,
  useNavigatorLanguage,
  useMapState as useObjectState,
  useOnClickRef,
  useOnHoverRef,
  useOnLongHover,
  useOnLongHover as useOnLongHoverRef,
  useOnLongPress,
  useOnLongPress as useOnLongPressRef,
  useOnWindowResize,
  useOnWindowScroll,
  useOnline,
  useOrientation,
  useOutsideClick,
  useOutsideClickRef,
  usePreviousDifferent,
  usePreviousImmediate,
  usePromise,
  useQueueState,
  useRaf,
  useRefElement,
  useRenderCount,
  useResizeObserverRef,
  useSafeSetState,
  useSelect,
  useSelectableList,
  useSessionstorageState,
  useSetState,
  useSpeech,
  useStackState,
  useThrottle,
  useTimeTravelState,
  useTimeoutWhen,
  useToggle,
  useUndoRedoState,
  useUndoState,
  useVibrate,
  useVideo,
  useWhyDidYouUpdate,
  useWillUnmount,
  useWindowEventListener,
  useWindowScrollPosition,
  useWindowSize
};
